// src/repository/graphQueries.js
// Centralized repository of Cypher queries for the Neo4j graph database

const commDateFilter = (commAlias = 'comm', startDateParam = '$startDate', endDateParam = '$endDate') => {
  return `date(${commAlias}.date) >= date(${startDateParam}) AND date(${commAlias}.date) <= date(${endDateParam})`;
};

export const clientQueries = {
  clientOverview: `
    MATCH (c:Communication {client_id: $clientId})
    WITH c ORDER BY c.date DESC LIMIT 10
    MATCH (c)-[r:EVIDENCED_BY]->(n)
    RETURN c, r, n
  `,

  communicationFrequency: `
    MATCH (c:Communication {client_id: $clientId})
    RETURN date(c.date) as day, count(c) as count
    ORDER BY day ASC
  `,

  listAllClients: `
    MATCH (n) WHERE n.client_id IS NOT NULL
    RETURN DISTINCT n.client_id AS clientId
    ORDER BY clientId
  `,

  getAllClientsWithStats: `
    MATCH (n) WHERE n.client_id IS NOT NULL
    WITH DISTINCT n.client_id AS clientName
    ORDER BY clientName ASC

    CALL {
        WITH clientName
        OPTIONAL MATCH (g:Goal {client_id: clientName, status: 'open'})
        RETURN count(DISTINCT g) AS goalCount
    }
    CALL {
        WITH clientName
        OPTIONAL MATCH (ai:ActionItem {client_id: clientName, status: 'open'})
        RETURN count(DISTINCT ai) AS actionItemCount
    }
    CALL {
        WITH clientName
        OPTIONAL MATCH (iss:Issue {client_id: clientName}) WHERE iss.status = 'open'
        RETURN count(DISTINCT iss) AS blockerCount
    }
    CALL {
        WITH clientName
        OPTIONAL MATCH (ini:Initiative {client_id: clientName}) WHERE ini.status = 'open'
        RETURN count(DISTINCT ini) AS initiativeCount
    }
    RETURN clientName AS name, goalCount, actionItemCount, blockerCount, initiativeCount
  `,
};

export const goalQueries = {
  getAllGoalsList: `
    MATCH (g:Goal {client_id: $clientId, status:'open'})
    OPTIONAL MATCH (i:Initiative {client_id: $clientId})-[:SUPPORTS_GOAL]->(g)
    OPTIONAL MATCH (iss:Issue {client_id: $clientId})-[:BLOCKS]->(i)
    WHERE iss.status = 'open'
    WITH g, count(DISTINCT i) AS initiatives, count(DISTINCT iss) AS blockers
    OPTIONAL MATCH (n)<-[:EVIDENCED_BY]-(c:Communication {client_id: $clientId})
    WHERE n=g OR (n:Initiative AND (n)-[:SUPPORTS_GOAL]->(g))
    WITH g, initiatives, blockers,
         coalesce(count(CASE WHEN c.date >= date()-duration('P30D') THEN c END),0) AS last30,
         coalesce(count(CASE WHEN c.date >= date()-duration('P60D')
                             AND c.date <  date()-duration('P30D') THEN c END),0) AS prev30
    OPTIONAL MATCH (g)<-[r2:EVIDENCED_BY]-(c2:Communication {client_id: $clientId})
    WHERE c2.date >= date()-duration('P30D')
    WITH g, initiatives, blockers, last30, prev30,
         avg(r2.sentiment_score) AS avgSent
    WITH g, initiatives, blockers, last30, prev30, avgSent,
         coalesce( toFloat(blockers) / (toFloat(blockers) + 2.0) , 0.0) AS blockedFactor,
         coalesce( 1.0 - avgSent , 0.0) AS negSent,
         coalesce( (toFloat(prev30) - toFloat(last30))/(toFloat(prev30)+1.0) , 0.0) AS neglectedFactor,
         CASE
             WHEN g.target_date IS NOT NULL AND date() <= g.target_date
                 THEN 1.0 / (toFloat(duration.between(date(), g.target_date).days) + 1.0)
             ELSE 0.0
         END AS timePressureFactor
    WITH g, initiatives, blockers,
         CASE
             WHEN (blockedFactor*0.4 + timePressureFactor*0 + negSent*0.3 + neglectedFactor*0.3) < 0 THEN 0
             ELSE (blockedFactor*0.4 + timePressureFactor*0 + negSent*0.3 + neglectedFactor*0.3)
         END AS priorityScore

    CALL {
        WITH g
        OPTIONAL MATCH (g)<-[:SUPPORTS_GOAL]-(:Initiative)<-[:SUPPORTS]-(ai:ActionItem)
        WHERE ai.assigned_to IS NOT NULL
        WITH g, collect({person: ai.assigned_to, score: 2}) as actionItemScores
        OPTIONAL MATCH (comm:Communication)-[:EVIDENCED_BY]->(g)
        WHERE comm.primary_staff IS NOT NULL
        MATCH (s:Staff) WHERE comm.primary_staff IN s.emails
        WITH g, actionItemScores, collect({person: s.name, score: 1}) as commScores
        UNWIND(actionItemScores + commScores) as scoreData
        WITH scoreData.person as person, sum(scoreData.score) as totalScore
        WHERE person IS NOT NULL
        ORDER BY totalScore DESC
        RETURN collect(person)[0] as calculatedDri
    }
    WITH g, g.target_date as targetDate, initiatives, blockers, priorityScore, COALESCE(g.assigned_to, calculatedDri) as dri
    WHERE ($driFilter IS NULL OR size($driFilter) = 0 OR ANY(filterDri IN $driFilter WHERE dri CONTAINS filterDri))
    
     OPTIONAL MATCH (node)<-[:EVIDENCED_BY]-(comm:Communication {client_id: $clientId})
     WHERE node = g OR (g)<-[:SUPPORTS_GOAL]-(node)
    WITH g, targetDate, initiatives, blockers, priorityScore, comm, dri
    RETURN g.id as id, g.name as name, g.description as description, g.status as status,
           targetDate,
           initiatives as initiativeCount,
           blockers as blockingIssuesCount,
           max(comm.date) as lastCommunicationDate,
           round(priorityScore, 3) AS score,
           dri as assigned_to
    ORDER BY score DESC, targetDate ASC
  `,
  getGoalsWithProgress: `
    MATCH (g:Goal {client_id: $clientId})
    OPTIONAL MATCH (i:Initiative)-[r:SUPPORTS_GOAL]->(g)
    RETURN g.id as goalId, g.name as goalName, g.description as goalDescription, 
           g.status as goalStatus, g.start_date as startDate, g.target_date as targetDate,
           count(i) as initiativeCount
    ORDER BY g.target_date ASC
  `,
  getActiveGoals: `
    MATCH (g:Goal {client_id: $clientId, status: 'open'})
    RETURN g
    ORDER BY g.target_date ASC
  `,
  getActiveGoalsCount: `
    MATCH (g:Goal {client_id: $clientId, status: 'open'})
    RETURN count(g) as count
  `,
  getGoalsAtRisk: `
    MATCH (g:Goal {client_id: $clientId, status:'open'}) 
    OPTIONAL MATCH (i:Initiative {client_id: $clientId})-[:SUPPORTS_GOAL]->(g)
    OPTIONAL MATCH (iss:Issue {client_id: $clientId})-[:BLOCKS]->(i)
    WHERE iss.status = 'open'
    WITH g, count(DISTINCT iss) AS blockers
    OPTIONAL MATCH (n)<-[:EVIDENCED_BY]-(c:Communication {client_id: $clientId})
    WHERE n=g OR (n:Initiative AND (n)-[:SUPPORTS_GOAL]->(g))
    WITH g, blockers,
        coalesce(count(CASE WHEN c.date >= date()-duration('P30D') THEN c END),0) AS last30,
        coalesce(count(CASE WHEN c.date >= date()-duration('P60D')
                            AND c.date <  date()-duration('P30D') THEN c END),0) AS prev30
    OPTIONAL MATCH (g)<-[r2:EVIDENCED_BY]-(c2:Communication {client_id: $clientId})
    WHERE c2.date >= date()-duration('P30D')
    WITH g, blockers, last30, prev30,
        avg(r2.sentiment_score) AS avgSent
    WITH g, blockers, last30, prev30, avgSent,
        coalesce( toFloat(blockers) / (toFloat(blockers) + 2.0) , 0.0) AS blockedFactor,
        coalesce( 1.0 - avgSent , 0.0) AS negSent,
        coalesce( (toFloat(prev30) - toFloat(last30))/(toFloat(prev30)+1.0) , 0.0) AS neglectedFactor,
        CASE
            WHEN g.target_date IS NOT NULL AND date() <= g.target_date
                THEN 1.0 / (toFloat(duration.between(date(), g.target_date).days) + 1.0)
            ELSE 0.0
        END AS timePressureFactor
    WITH g, 
        CASE
            WHEN (blockedFactor*0.4 + timePressureFactor*0 + negSent*0.3 + neglectedFactor*0.3) < 0 THEN 0
            ELSE (blockedFactor*0.4 + timePressureFactor*0 + negSent*0.3 + neglectedFactor*0.3)
        END AS priorityScore

    CALL {
        WITH g
        OPTIONAL MATCH (g)<-[:SUPPORTS_GOAL]-(:Initiative)<-[:SUPPORTS]-(ai:ActionItem)
        WHERE ai.assigned_to IS NOT NULL
        WITH g, collect({person: ai.assigned_to, score: 2}) as actionItemScores
        OPTIONAL MATCH (comm:Communication)-[:EVIDENCED_BY]->(g)
        WHERE comm.primary_staff IS NOT NULL
        MATCH (s:Staff) WHERE comm.primary_staff IN s.emails
        WITH g, actionItemScores, collect({person: s.name, score: 1}) as commScores
        UNWIND(actionItemScores + commScores) as scoreData
        WITH scoreData.person as person, sum(scoreData.score) as totalScore
        WHERE person IS NOT NULL
        ORDER BY totalScore DESC
        RETURN collect(person)[0] as calculatedDri
    }
    WITH g, priorityScore, COALESCE(g.assigned_to, calculatedDri) as dri
    WHERE ($driFilter IS NULL OR size($driFilter) = 0 OR ANY(filterDri IN $driFilter WHERE dri CONTAINS filterDri))

    RETURN g.id as goalId,
          g.name as goalName,
          round(priorityScore, 3) AS score
    ORDER BY score DESC
    LIMIT 5
  `,
  getGoalSentiment: `
    MATCH (c:Communication {client_id: $clientId})
    WHERE c.date >= datetime() - duration({days: $daysAgo})
    MATCH (c)-[r:EVIDENCED_BY]->(g:Goal {id: $goalId})
    RETURN c.date as date, r.sentiment_score as sentiment
    ORDER BY c.date ASC
  `,
  getGoalInitiativeIssueGraph: `
    MATCH (g:Goal {client_id: $clientId})
    WHERE g.status = 'open'
    OPTIONAL MATCH (i:Initiative {client_id: $clientId})-[:SUPPORTS_GOAL]->(g)
    OPTIONAL MATCH (iss:Issue {client_id: $clientId})-[:BLOCKS]->(i)
    WHERE iss.status = 'open'
    WITH collect(DISTINCT g) + collect(DISTINCT i) + collect(DISTINCT iss) as nodeCollection
    UNWIND nodeCollection as n
    OPTIONAL MATCH (n)-[r:SUPPORTS_GOAL|BLOCKS]->(m)
    WHERE m IN nodeCollection
    RETURN DISTINCT n, r, m
  `,
  getGoalDetails_Initiatives: `
    MATCH (i:Initiative {client_id: $clientId})-[:SUPPORTS_GOAL]->(g:Goal {id: $goalId, client_id: $clientId})
    RETURN i.id as id, i.name as name, i.status as status, i.description as description
    ORDER BY i.name
  `,
  getGoalDetails_BlockingIssues: `
    MATCH (iss:Issue {client_id: $clientId})-[:BLOCKS]->(i:Initiative {client_id: $clientId})-[:SUPPORTS_GOAL]->(g:Goal {id: $goalId, client_id: $clientId})
    WHERE iss.status = 'open'
    RETURN DISTINCT iss.id as id, iss.name as name, iss.status as status, iss.severity as severity, i.name as blockedInitiativeName
    ORDER BY iss.severity, iss.name
  `,
  getGoalDetails_RecentComms: `
    MATCH (g:Goal {id: $goalId, client_id: $clientId})
    OPTIONAL MATCH (i:Initiative {client_id: $clientId})-[:SUPPORTS_GOAL]->(g)
    WITH g, collect(i) AS initiatives
    WITH g + initiatives AS relevantNodes
    UNWIND relevantNodes as node
    MATCH (node)<-[r:EVIDENCED_BY]-(comm:Communication {client_id: $clientId})
    WHERE node IS NOT NULL
      AND comm.date >= datetime() - duration({days: 90})
    RETURN DISTINCT comm.id as id, comm.date as date, comm.record_type as type, comm.summary as summary, r.text_snippet as snippet
    ORDER BY comm.date DESC
    LIMIT 10
  `,
};

export const initiativeQueries = {
  getAllInitiativesList: `
    MATCH (i:Initiative {client_id: $clientId})
    OPTIONAL MATCH (c:Communication {client_id: $clientId})-[:EVIDENCED_BY]->(i)
    WITH i,
         coalesce(count(DISTINCT CASE WHEN c.date >= date()-duration('P30D') THEN c END),0) AS last30,
         coalesce(count(DISTINCT CASE WHEN c.date >= date()-duration('P60D')
                                      AND c.date <  date()-duration('P30D') THEN c END),0) AS prev30
    OPTIONAL MATCH (iss:Issue {client_id: $clientId})-[:BLOCKS]->(i)
    WHERE iss.status = 'open'
    WITH i, last30, prev30,
         count(DISTINCT iss) AS blockers
    WITH i, last30, prev30, blockers,
         coalesce( (toFloat(prev30) - toFloat(last30))/(toFloat(prev30)+1.0) , 0.0) AS velocityRisk,
         coalesce( toFloat(blockers) / (toFloat(blockers) + 2.0) , 0.0) AS blockerFactor
    WITH i, blockers,
         CASE
            WHEN (velocityRisk*0.6 + blockerFactor*0.4) < 0 THEN 0
            ELSE (velocityRisk*0.6 + blockerFactor*0.4)
         END AS momentumScore

    CALL {
        WITH i
        OPTIONAL MATCH (i)<-[:SUPPORTS]-(ai:ActionItem)
        WHERE ai.assigned_to IS NOT NULL
        WITH i, collect({person: ai.assigned_to, score: 2}) as actionItemScores
        OPTIONAL MATCH (comm:Communication)-[:EVIDENCED_BY]->(i)
        WHERE comm.primary_staff IS NOT NULL
        MATCH (s:Staff) WHERE comm.primary_staff IN s.emails
        WITH i, actionItemScores, collect({person: s.name, score: 1}) as commScores
        UNWIND(actionItemScores + commScores) as scoreData
        WITH scoreData.person as person, sum(scoreData.score) as totalScore
        WHERE person IS NOT NULL
        ORDER BY totalScore DESC
        RETURN collect(person)[0] as calculatedDri
    }
    WITH i, momentumScore, blockers, COALESCE(i.assigned_to, calculatedDri) as dri
    WHERE ($driFilter IS NULL OR size($driFilter) = 0 OR ANY(filterDri IN $driFilter WHERE dri CONTAINS filterDri))

    OPTIONAL MATCH (i)-[:SUPPORTS_GOAL]->(g:Goal {client_id: $clientId})
    OPTIONAL MATCH (a:ActionItem {client_id: $clientId})-[:SUPPORTS]->(i)
    RETURN i.id as id, i.name as name, i.description as description, i.status as status,
           collect(DISTINCT g.name) as supportedGoals,
           blockers as blockingIssuesCount,
           count(DISTINCT a) as actionItemCount,
           round(momentumScore, 3) AS score,
           dri as assigned_to
    ORDER BY score DESC, i.name ASC
  `,
  getInitiatives: `
    MATCH (i:Initiative {client_id: $clientId})
    OPTIONAL MATCH (issue:Issue)-[blocks:BLOCKS]->(i)
    OPTIONAL MATCH (i)-[supports:SUPPORTS_GOAL]->(g:Goal)
    RETURN i.id as initiativeId, i.name as initiativeName, 
           i.description as initiativeDescription, i.status as initiativeStatus,
           count(distinct issue) as blockingIssuesCount,
           collect(distinct g.name) as supportedGoals
    ORDER BY i.status, blockingIssuesCount DESC
  `,
  getBlockedInitiatives: `
    MATCH (i:Initiative {client_id: $clientId, status: 'open'})
    MATCH (issue:Issue)-[r:BLOCKS]->(i)
    RETURN i, r, issue
  `,
  getDistinctInitiatives: `
    MATCH (i:Initiative {client_id: $clientId})
    WHERE i.name IS NOT NULL AND i.name <> ''
    RETURN DISTINCT i.name as initiativeName
    ORDER BY initiativeName ASC
  `,
  getDistinctInitiativesByDepartment: `
    MATCH (s:Staff {department: $departmentId})
    MATCH (a:ActionItem)
    WHERE a.assigned_to = s.name
    MATCH (a)-[:SUPPORTS]->(i:Initiative)
    RETURN DISTINCT i.name AS initiativeName
    ORDER BY initiativeName ASC
  `,
};

export const actionItemQueries = {
  getAllPendingActionItemsList: `
    MATCH (a:ActionItem {client_id: $clientId, status: 'open'})
    OPTIONAL MATCH (a)-[:SUPPORTS]->(i_filter:Initiative {client_id: $clientId})
    WHERE ($driFilter IS NULL OR size($driFilter) = 0 OR ANY(filterDri IN $driFilter WHERE a.assigned_to CONTAINS filterDri))
      AND ($initiativeFilter IS NULL OR $initiativeFilter = '' OR i_filter.name = $initiativeFilter)
    OPTIONAL MATCH (c:Communication {client_id: $clientId})-[:EVIDENCED_BY]->(a)
    WITH a,
         coalesce(count(DISTINCT CASE WHEN c.date >= date()-duration('P30D') THEN c END),0) AS last30,
         coalesce(count(DISTINCT CASE WHEN c.date >= date()-duration('P60D')
                                      AND c.date <  date()-duration('P30D') THEN c END),0) AS prev30
    OPTIONAL MATCH (c30:Communication {client_id: $clientId})-[r:EVIDENCED_BY]->(a)
    WHERE c30.date >= date()-duration('P30D')
    WITH a, last30, prev30,
         avg(r.sentiment_score) AS avgSent
    OPTIONAL MATCH (a)-[:SUPPORTS]->(init:Initiative {client_id: $clientId})
    OPTIONAL MATCH (iss:Issue {client_id: $clientId})-[:BLOCKS]->(init)
    WHERE iss.status = 'open'
    WITH a, last30, prev30, avgSent,
         max(init.name) AS initiativeName, 
         size(collect(DISTINCT init)) AS initCount,
         count(DISTINCT iss) AS initBlockers
    WITH a, last30, prev30, avgSent, initCount, initBlockers, initiativeName,
         CASE
             WHEN a.deadline IS NULL THEN NULL
             WHEN date() > a.deadline THEN
                 toFloat(duration.between(a.deadline, date()).days) / (toFloat(duration.between(a.deadline, date()).days) + 3.0)
             WHEN duration.between(date(), a.deadline).days <= 7 THEN
                 (7.0 - toFloat(duration.between(date(), a.deadline).days)) / 7.0
             ELSE 0.0
         END AS deadlinePressure,
         CASE
             WHEN (toFloat(last30) - toFloat(prev30)) / (toFloat(prev30) + 1.0) > 0.0
             THEN (toFloat(last30) - toFloat(prev30)) / (toFloat(prev30) + 1.0)
             ELSE 0.0
         END AS mentionSpike,
         coalesce(1.0 - avgSent, 0.0) AS negSent,
         CASE
             WHEN initCount = 0 THEN 0.0
             ELSE toFloat(initBlockers) / (toFloat(initBlockers) + 2.0)
         END AS initFactor
    WITH a, deadlinePressure, mentionSpike, negSent, initFactor, initiativeName,
         0.40 AS w_init,
         CASE WHEN deadlinePressure IS NULL THEN 0.0 ELSE 0.30 END AS w_dead,
         0.20 AS w_spike,
         0.10 AS w_sent,
         (0.40 + (CASE WHEN deadlinePressure IS NULL THEN 0.0 ELSE 0.30 END) + 0.20 + 0.10) as w_sum
    WITH a, initiativeName,
         CASE
            WHEN w_sum = 0 THEN 0
            ELSE (
                initFactor * w_init +
                coalesce(deadlinePressure, 0.0) * w_dead +
                mentionSpike * w_spike +
                negSent * w_sent
            ) / w_sum
         END AS urgencyScore
    RETURN a.id as id, a.name as name, a.description as description,
           a.assigned_to as assignee, a.deadline as deadline, a.status as status, 
           initiativeName,
           round(urgencyScore, 3) AS score
    ORDER BY score DESC, a.deadline ASC, a.name ASC
  `,
  getActionItemsByStatus: `
    MATCH (a:ActionItem {client_id: $clientId})
    RETURN a.status as status, count(a) as count
  `,
  getActionItemsByAssignee: `
    MATCH (a:ActionItem {client_id: $clientId})
    WHERE a.status = 'open'
    RETURN a.assigned_to as assignee, count(a) as count
    ORDER BY count DESC
  `,
  getOverdueActionItems: `
    MATCH (a:ActionItem {client_id: $clientId, status: 'open'})
    WHERE a.deadline < date()
    RETURN a.name as actionName, a.assigned_to as assignee,
           duration.between(a.deadline, date()).days as daysOverdue
    ORDER BY daysOverdue DESC
    LIMIT 10
  `,
  getActionItemDetails: `
    MATCH (a:ActionItem {client_id: $clientId, id: $actionItemId})
    OPTIONAL MATCH (a)-[s:SUPPORTS]->(i:Initiative)
    OPTIONAL MATCH (c:Communication)-[e:EVIDENCED_BY]->(a)
    RETURN a, 
           collect(distinct i) as initiatives,
           collect(distinct {communication: c, evidence: e}) as evidences
  `,
  getActionItemsFilteredByStatus: `
    MATCH (a:ActionItem {client_id: $clientId, status: $status})
    OPTIONAL MATCH (a)-[:SUPPORTS]->(i:Initiative)
    RETURN a.id as id,
           a.name as name,
           a.description as description,
           a.assigned_to as assignee,
           a.deadline as deadline,
           i.name as initiativeName
    ORDER BY a.deadline ASC, a.name ASC
    LIMIT 50
  `,
  getDistinctAssigneesByDepartment: `
    MATCH (s:Staff {department: $departmentId})
    MATCH (a:ActionItem {assigned_to: s.name})
    RETURN DISTINCT s.name AS assignee
    ORDER BY assignee
  `,
};

export const issueQueries = {
  getAllIssuesList: `
    MATCH (iss:Issue {client_id: $clientId})
    WHERE ($severityFilter IS NULL OR $severityFilter = '' OR iss.severity = $severityFilter)
      AND (
        ($statusFilter IS NULL OR $statusFilter = '' OR iss.status = $statusFilter)
      )
    AND (
      EXISTS { (comm_iss:Communication {client_id: $clientId})-[:EVIDENCED_BY]->(iss) WHERE ${commDateFilter('comm_iss', '$startDate', '$endDate')} }
      OR EXISTS {
        (iss)-[:BLOCKS]->(init_blocked:Initiative {client_id: $clientId})
        WHERE EXISTS { (comm_init_b:Communication {client_id: $clientId})-[:EVIDENCED_BY]->(init_blocked) WHERE ${commDateFilter('comm_init_b', '$startDate', '$endDate')} }
      }
    )
    WITH iss, size([(iss)-[]-() | 1]) + size([()-[]-(iss) | 1]) as connectionCount
    CALL {
        WITH iss
        OPTIONAL MATCH (comm:Communication)-[:EVIDENCED_BY]->(iss)
        WHERE comm.primary_staff IS NOT NULL
        MATCH (s:Staff) WHERE comm.primary_staff IN s.emails
        WITH iss, collect(s.name) as people
        RETURN head(people) as calculatedDri
    }
    WITH iss, connectionCount, COALESCE(iss.assigned_to, calculatedDri) as dri
    WHERE ($driFilter IS NULL OR size($driFilter) = 0 OR ANY(filterDri IN $driFilter WHERE dri CONTAINS filterDri))

    OPTIONAL MATCH (iss)-[:BLOCKS]->(init_link:Initiative {client_id: $clientId})
    RETURN iss.id as id, iss.name as name, iss.description as description,
          iss.severity as severity, iss.status as status,
          collect(DISTINCT init_link.name) as blockedInitiatives,
          connectionCount AS score,
          dri as assigned_to
    ORDER BY score DESC, iss.severity, iss.name ASC
  `,
  getIssuesBySeverity: `
    MATCH (i:Issue {client_id: $clientId})
    RETURN i.severity as severity, count(i) as count
  `,
  getIssueDetails: `
    MATCH (i:Issue {client_id: $clientId, id: $issueId})
    OPTIONAL MATCH (i)-[b:BLOCKS]->(initiative:Initiative)
    OPTIONAL MATCH (c:Communication)-[e:EVIDENCED_BY]->(i)
    RETURN i, 
           collect(distinct initiative) as blockedInitiatives,
           collect(distinct {communication: c, evidence: e}) as evidences
  `,
  getHighSeverityIssues: `
    MATCH (i:Issue {client_id: $clientId, severity: 'high'})
    OPTIONAL MATCH (i)-[b:BLOCKS]->(initiative:Initiative)
    RETURN i.id as issueId, i.name as issueName, i.description as issueDescription,
           i.status as issueStatus, count(initiative) as blockedInitiativesCount
    ORDER BY blockedInitiativesCount DESC
  `
};

export const sentimentQueries = {
  getSentimentOverTime: `
    MATCH (c:Communication {client_id: $clientId})-[r:EVIDENCED_BY]->(n)
    WITH date.truncate('week', c.date) as weekBeginning, avg(r.sentiment_score) as avgSentiment
    ORDER BY weekBeginning ASC
    RETURN weekBeginning, avgSentiment
  `,
  getRecentSentimentTrend: `
    MATCH (c:Communication {client_id: $clientId})
    WHERE c.date >= datetime() - duration({days: 30})
    MATCH (c)-[r:EVIDENCED_BY]->(n)
    WITH date(c.date) as day, avg(r.sentiment_score) as avgSentiment
    ORDER BY day ASC
    RETURN collect(day) as days, collect(avgSentiment) as sentiments
  `,
  getSentimentByEntityType: `
    MATCH (c:Communication {client_id: $clientId})
    WHERE c.date >= datetime() - duration({days: $daysAgo})
    MATCH (c)-[r:EVIDENCED_BY]->(n)
    RETURN 
      CASE 
        WHEN n:Goal THEN 'Goal' 
        WHEN n:Initiative THEN 'Initiative'
        WHEN n:ActionItem THEN 'Action Item'
        WHEN n:Issue THEN 'Issue'
        ELSE 'Other'
      END as entityType,
      avg(r.sentiment_score) as avgSentiment,
      count(r) as mentionCount
    ORDER BY avgSentiment ASC
  `,
  getAllCommunicationsList: `
    MATCH (c:Communication {client_id: $clientId})
    OPTIONAL MATCH (eval:CallEval)-[:EVALUATES]->(c)
    OPTIONAL MATCH (s_eval:Staff {id: eval.staff_id})
    WITH c, collect(DISTINCT
      CASE
        WHEN eval IS NOT NULL THEN {
          staffId: eval.staff_id,
          staffName: s_eval.name,
          scores: {
            overall_score: eval.overall_score,
            scarf_status_score: eval.scarf_status_score,
            scarf_certainty_score: eval.scarf_certainty_score,
            scarf_autonomy_score: eval.scarf_autonomy_score,
            scarf_relatedness_score: eval.scarf_relatedness_score,
            scarf_fairness_score: eval.scarf_fairness_score,
            empathy_score: eval.empathy_score,
            assertiveness_score: eval.assertiveness_score,
            respect_score: eval.respect_score,
            clarity_score: eval.clarity_score,
            call_structure_opening_score: eval.call_structure_opening_score,
            call_structure_info_gathering_score: eval.call_structure_info_gathering_score,
            call_structure_solution_presentation_score: eval.call_structure_solution_presentation_score,
            call_structure_closing_score: eval.call_structure_closing_score,
             id: eval.id 
          }
        }
        ELSE null
      END
    ) AS callEvaluations
    OPTIONAL MATCH (c)-[r:EVIDENCED_BY]->(n)
    RETURN c.id as id,
           c.date as date,
           c.record_type as type,
           c.primary_staff as staff,
           c.title as title,
           c.summary as summary,
           c.participants as participants,
           [eval IN callEvaluations WHERE eval IS NOT NULL] as callEvaluations,
           collect(DISTINCT {
               type: labels(n)[0],
               name: n.name,
               sentiment: r.sentiment_score
           }) as evidencedEntities
    ORDER BY c.date DESC
    LIMIT 500
 `,
};

export const insightQueries = {
  getDecliningTrend: `
    MATCH (c:Communication {client_id: $clientId})
    WHERE c.date >= datetime() - duration({days: 30})
    MATCH (c)-[r:EVIDENCED_BY]->(n)
    WITH date(c.date) as day, avg(r.sentiment_score) as avgSentiment
    ORDER BY day ASC
    WITH collect(avgSentiment) as sentiments
    WHERE size(sentiments) >= 3 AND 
          sentiments[size(sentiments)-1] < sentiments[size(sentiments)-2] AND
          sentiments[size(sentiments)-2] < sentiments[size(sentiments)-3]
    RETURN "Sentiment has declined for 3 consecutive periods" as insight
  `,
  getLongBlockedInitiatives: `
    MATCH (i:Initiative {client_id: $clientId})
    MATCH (issue:Issue)-[r:BLOCKS]->(i)
    WITH i, issue, duration.between(r.linked_at, datetime()).days as blockingDays
    WHERE issue.severity = 'high' AND blockingDays > 7
    RETURN i.name as initiative, issue.name as blockingIssue, blockingDays,
           issue.severity as severity
    ORDER BY blockingDays DESC
  `,
  getStaffFocusDistribution: `
    MATCH (c:Communication {client_id: $clientId})
    WHERE c.date >= datetime() - duration({days: 30})
    MATCH (c)-[:EVIDENCED_BY]->(n)
    WHERE c.primary_staff IS NOT NULL
    RETURN c.primary_staff as staff, 
           count(DISTINCT CASE WHEN n:Goal THEN n END) as goalCount,
           count(DISTINCT CASE WHEN n:Initiative THEN n END) as initiativeCount,
           count(DISTINCT CASE WHEN n:ActionItem THEN n END) as actionItemCount,
           count(DISTINCT CASE WHEN n:Issue THEN n END) as issueCount
    ORDER BY goalCount + initiativeCount + actionItemCount + issueCount DESC
  `,
  getUnaddressedInitiatives: `
    MATCH (i:Initiative {client_id: $clientId, status: 'open'})
    OPTIONAL MATCH (a:ActionItem)-[:SUPPORTS]->(i)
    WITH i, count(a) as actionItemCount
    WHERE actionItemCount = 0
    RETURN i.name as initiativeName, i.description as initiativeDescription,
           "No action items defined" as insight
  `,
  getApproachingDeadlines: `
    MATCH (g:Goal {client_id: $clientId, status: 'open'})
    WHERE g.target_date IS NOT NULL AND 
          duration.between(date(), g.target_date).days <= 30
    OPTIONAL MATCH (i:Initiative)-[:SUPPORTS_GOAL]->(g)
    OPTIONAL MATCH (issue:Issue)-[:BLOCKS]->(i)
    WITH g, count(distinct i) as initiativeCount, 
         count(distinct issue) as blockingIssuesCount,
         duration.between(date(), g.target_date).days as daysRemaining
    RETURN g.name as goalName, g.target_date as targetDate,
           daysRemaining, initiativeCount, blockingIssuesCount
    ORDER BY daysRemaining ASC
  `
};

export const networkQueries = {
  networkData: `
    MATCH (n {client_id: $clientId}) 
    WHERE n:Goal OR n:Initiative OR n:Issue OR n:ActionItem
    OPTIONAL MATCH (n)-[r]->(m {client_id: $clientId})
    WHERE type(r) IN ['SUPPORTS_GOAL', 'BLOCKS', 'SUPPORTS']
    RETURN n, r, m
  `,
  communicationEvidence: `
    MATCH (c:Communication {client_id: $clientId})
    WHERE c.date >= datetime() - duration({days: $daysAgo})
    MATCH (c)-[r:EVIDENCED_BY]->(n)
    RETURN c, r, n
  `
};

export const overviewQueries = {
  getCommunicationFrequencyByType: `
    MATCH (c:Communication {client_id: $clientId})
    WHERE c.date >= datetime() - duration('P100D') 
    WITH date.truncate('week', c.date) AS weekBeginning,
          coalesce(c.record_type, 'Unknown') AS commType,
          count(c) AS weeklyCount
    ORDER BY weekBeginning ASC, commType ASC
    RETURN weekBeginning, commType, weeklyCount
  `,
  getPriorityTotalIssuesCount: `
    MATCH (i:Issue {client_id: $clientId})
    RETURN count(i) as count
  `,
  getPriorityCriticalIssuesCount: `
    MATCH (i:Issue {client_id: $clientId, severity: 'high', status: 'open'})
    RETURN count(i) as count
  `,
  getPriorityTotalInitiativesCount: `
    MATCH (i:Initiative {client_id: $clientId})
    RETURN count(DISTINCT i) as count
  `,
  getPriorityBlockedInitiativesCount: `
    MATCH (i:Initiative {client_id: $clientId})<-[:BLOCKS]-(iss:Issue)
    WHERE i.status = 'open' 
      AND iss.status = 'open' 
    RETURN count(DISTINCT i) as count
  `,
  getPriorityOverdueActionsCount: `
    MATCH (a:ActionItem {client_id: $clientId, status: 'open'})
    WHERE a.deadline IS NOT NULL AND a.deadline < date()
    RETURN count(a) as count
  `,
  getPriorityNeglectedGoalsCount: `
    WITH date() - duration({days: 14}) AS neglectThreshold
    MATCH (g:Goal {client_id: $clientId, status: 'open'})
    WHERE NOT EXISTS {
      MATCH (c:Communication)-[:EVIDENCED_BY]->(g)
      WHERE c.client_id = $clientId AND c.date >= neglectThreshold
    } AND NOT EXISTS {
      MATCH (g)<-[:SUPPORTS_GOAL]-(i:Initiative)
      MATCH (c:Communication)-[:EVIDENCED_BY]->(i)
      WHERE c.client_id = $clientId AND c.date >= neglectThreshold
    }
    RETURN count(g) as count
  `,
  getIssuesByRiskScore: `
    MATCH (c:Communication {client_id: $clientId})-[r:EVIDENCED_BY]->(iss:Issue {client_id: $clientId})
    WHERE iss.status = 'open' 
    WITH iss,
        avg(r.sentiment_score) AS avgSent,
        count(c) AS totalMentions,
        count(CASE WHEN c.date >= date() - duration('P7D') THEN c END) AS last7Mentions,
        count(CASE WHEN c.date >= date() - duration('P14D') AND c.date < date() - duration('P7D') THEN c END) AS prev7Mentions
    WITH iss, totalMentions, last7Mentions, prev7Mentions,
        coalesce((1.0 - avgSent) / 2.0, 0.5) AS sentimentFactor, 
        coalesce( (toFloat(last7Mentions) - toFloat(prev7Mentions)) / (toFloat(prev7Mentions) + 1.0), 0.0) AS trendFactor,
        log(toFloat(totalMentions) + 1.0) AS volumeFactor 
    WITH iss, sentimentFactor, trendFactor, volumeFactor,
        CASE
            WHEN (sentimentFactor * 0.5) + (trendFactor * 0.3) + (volumeFactor * 0.2) < 0 THEN 0
            ELSE (sentimentFactor * 0.5) + (trendFactor * 0.3) + (volumeFactor * 0.2)
        END AS riskScore
        
    CALL {
        WITH iss
        OPTIONAL MATCH (comm:Communication)-[:EVIDENCED_BY]->(iss)
        WHERE comm.primary_staff IS NOT NULL
        MATCH (s:Staff) WHERE comm.primary_staff IN s.emails
        WITH iss, collect(s.name) as people
        RETURN head(people) as calculatedDri
    }
    WITH iss, riskScore, COALESCE(iss.assigned_to, calculatedDri) as dri
    WHERE ($driFilter IS NULL OR size($driFilter) = 0 OR ANY(filterDri IN $driFilter WHERE dri CONTAINS filterDri))

    RETURN iss.id as issueId,        
          iss.name AS issueName,
          round(riskScore, 2) AS riskScore
    ORDER BY riskScore DESC
    LIMIT 10 
  `
};

export const entityDetailQueries = {
  getEntityMentionTrend: `
        MATCH (entity {id: $entityId, client_id: $clientId}) 
        MATCH (c:Communication {client_id: $clientId})-[:EVIDENCED_BY]->(entity)
        WITH date.truncate('week', c.date) AS wk, count(c) AS mentions
        RETURN wk AS weekBeginning, mentions 
        ORDER BY wk ASC
    `,
  getEntitySentimentTrend: `
        MATCH (entity {id: $entityId, client_id: $clientId}) 
        MATCH (c:Communication {client_id: $clientId})-[r:EVIDENCED_BY]->(entity)
        WITH date.truncate('week', c.date) AS wk, avg(r.sentiment_score) AS weeklySentiment
        RETURN wk, round(weeklySentiment, 3) AS avgSentiment
        ORDER BY wk ASC
    `,
  getEntityLinkedComms: `
        MATCH (entity {id: $entityId, client_id: $clientId})<-[r:EVIDENCED_BY]-(c:Communication {client_id: $clientId})
        OPTIONAL MATCH (eval:CallEval)-[:EVALUATES]->(c)
        OPTIONAL MATCH (s_eval:Staff {id: eval.staff_id})
        WITH c, r, entity,
             collect(DISTINCT
                CASE WHEN eval IS NOT NULL THEN {
                    staffName: s_eval.name,
                    staffId: eval.staff_id,
                    scores: {
                        overall_score: eval.overall_score,
                        scarf_status_score: eval.scarf_status_score,
                        scarf_certainty_score: eval.scarf_certainty_score,
                        scarf_autonomy_score: eval.scarf_autonomy_score,
                        scarf_relatedness_score: eval.scarf_relatedness_score,
                        scarf_fairness_score: eval.scarf_fairness_score,
                        empathy_score: eval.empathy_score,
                        assertiveness_score: eval.assertiveness_score,
                        respect_score: eval.respect_score,
                        clarity_score: eval.clarity_score,
                        call_structure_opening_score: eval.call_structure_opening_score,
                        call_structure_info_gathering_score: eval.call_structure_info_gathering_score,
                        call_structure_solution_presentation_score: eval.call_structure_solution_presentation_score,
                        call_structure_closing_score: eval.call_structure_closing_score,
                        id: eval.id
                    }
                } ELSE null END
             ) as callEvaluations
        WITH c, entity,
             collect(r.text_snippet) as snippets,
             [evl IN callEvaluations WHERE evl IS NOT NULL] as finalEvaluations
        ORDER BY c.date DESC
        LIMIT 20
        RETURN c.id as id,
               c.date as date,
               c.record_type as type,
               c.title as title,
               c.primary_staff as staff,
               c.summary as summary,
               finalEvaluations as callEvaluations,
               snippets
        ORDER BY c.date DESC`,
  getGoalDetails: ` 
        MATCH (g:Goal {id: $entityId, client_id: $clientId})
        RETURN g.id as id, g.name as name, g.description as description, g.status as status, g.target_date as targetDate
        LIMIT 1
    `,
  getGoalSupportingInitiatives: `
        MATCH (i:Initiative {client_id: $clientId})-[:SUPPORTS_GOAL]->(g:Goal {id: $entityId, client_id: $clientId})
        RETURN i.id as id, i.name as name, i.status as status
        ORDER BY i.name
    `,
  getGoalBlockingIssues: ` 
        MATCH (iss:Issue {client_id: $clientId})-[:BLOCKS]->(i:Initiative {client_id: $clientId})-[:SUPPORTS_GOAL]->(g:Goal {id: $entityId, client_id: $clientId})
        WHERE iss.status = 'open'
        RETURN DISTINCT iss.id as id, iss.name as name, iss.status as status, iss.severity as severity
        ORDER BY iss.severity, iss.name
    `,
  getGoalMentionTrend: '/* Uses getEntityMentionTrend logic */\n' + `
        MATCH (entity:Goal {id: $entityId, client_id: $clientId})
        MATCH (c:Communication {client_id: $clientId})-[:EVIDENCED_BY]->(entity)
        WITH date.truncate('week', c.date) AS wk, count(c) AS mentions
        RETURN wk AS weekBeginning, mentions ORDER BY wk ASC`,
  getGoalSentimentTrend: '/* Uses getEntitySentimentTrend logic */\n' + `
        MATCH (entity:Goal {id: $entityId, client_id: $clientId})
        MATCH (c:Communication {client_id: $clientId})-[r:EVIDENCED_BY]->(entity)
        WITH date.truncate('week', c.date) AS wk, avg(r.sentiment_score) AS weeklySentiment
        RETURN wk, round(weeklySentiment, 3) AS avgSentiment ORDER BY wk ASC`,
  getGoalLinkedComms: '/* Uses getEntityLinkedComms logic */\n' + `
        MATCH (entity:Goal {id: $entityId, client_id: $clientId})<-[r:EVIDENCED_BY]-(c:Communication {client_id: $clientId})
        OPTIONAL MATCH (eval:CallEval)-[:EVALUATES]->(c) OPTIONAL MATCH (s_eval:Staff {id: eval.staff_id})
        WITH c, r, entity, collect(DISTINCT CASE WHEN eval IS NOT NULL THEN { staffName: s_eval.name, staffId: eval.staff_id, scores: { overall_score: eval.overall_score, scarf_status_score: eval.scarf_status_score, scarf_certainty_score: eval.scarf_certainty_score, scarf_autonomy_score: eval.scarf_autonomy_score, scarf_relatedness_score: eval.scarf_relatedness_score, scarf_fairness_score: eval.scarf_fairness_score, empathy_score: eval.empathy_score, assertiveness_score: eval.assertiveness_score, respect_score: eval.respect_score, clarity_score: eval.clarity_score, call_structure_opening_score: eval.call_structure_opening_score, call_structure_info_gathering_score: eval.call_structure_info_gathering_score, call_structure_solution_presentation_score: eval.call_structure_solution_presentation_score, call_structure_closing_score: eval.call_structure_closing_score, id: eval.id } } ELSE null END ) as callEvaluations
        WITH c, entity, collect(r.text_snippet) as snippets, [evl IN callEvaluations WHERE evl IS NOT NULL] as finalEvaluations
        ORDER BY c.date DESC LIMIT 20
        RETURN c.id as id, c.date as date, c.record_type as type, c.title as title, c.primary_staff as staff, c.summary as summary, finalEvaluations as callEvaluations, snippets
        ORDER BY c.date DESC`,
  getInitiativeDetails: `
        MATCH (i:Initiative {id: $entityId, client_id: $clientId})
        OPTIONAL MATCH (i)-[:SUPPORTS_GOAL]->(g:Goal {client_id: $clientId})
        RETURN i.id as id, i.name as name, i.description as description, i.status as status, collect(g.name) as supportedGoals
        LIMIT 1
    `,
  getInitiativeBlockingIssues: ` 
        MATCH (iss:Issue {client_id: $clientId})-[:BLOCKS]->(i:Initiative {id: $entityId, client_id: $clientId})
        WHERE iss.status = 'open'
        RETURN iss.id as id, iss.name as name, iss.status as status, iss.severity as severity
        ORDER BY iss.severity, iss.name
    `,
  getInitiativeMentionTrend: '/* Uses getEntityMentionTrend logic */\n' + `
        MATCH (entity:Initiative {id: $entityId, client_id: $clientId})
        MATCH (c:Communication {client_id: $clientId})-[:EVIDENCED_BY]->(entity)
        WITH date.truncate('week', c.date) AS wk, count(c) AS mentions
        RETURN wk AS weekBeginning, mentions ORDER BY wk ASC`,
  getInitiativeSentimentTrend: '/* Uses getEntitySentimentTrend logic */\n' + `
        MATCH (entity:Initiative {id: $entityId, client_id: $clientId})
        MATCH (c:Communication {client_id: $clientId})-[r:EVIDENCED_BY]->(entity)
        WITH date.truncate('week', c.date) AS wk, avg(r.sentiment_score) AS weeklySentiment
        RETURN wk, round(weeklySentiment, 3) AS avgSentiment ORDER BY wk ASC`,
  getInitiativeLinkedComms: '/* Uses getEntityLinkedComms logic */\n' + `
        MATCH (entity:Initiative {id: $entityId, client_id: $clientId})<-[r:EVIDENCED_BY]-(c:Communication {client_id: $clientId})
        OPTIONAL MATCH (eval:CallEval)-[:EVALUATES]->(c) OPTIONAL MATCH (s_eval:Staff {id: eval.staff_id})
        WITH c, r, entity, collect(DISTINCT CASE WHEN eval IS NOT NULL THEN { staffName: s_eval.name, staffId: eval.staff_id, scores: { overall_score: eval.overall_score, scarf_status_score: eval.scarf_status_score, scarf_certainty_score: eval.scarf_certainty_score, scarf_autonomy_score: eval.scarf_autonomy_score, scarf_relatedness_score: eval.scarf_relatedness_score, scarf_fairness_score: eval.scarf_fairness_score, empathy_score: eval.empathy_score, assertiveness_score: eval.assertiveness_score, respect_score: eval.respect_score, clarity_score: eval.clarity_score, call_structure_opening_score: eval.call_structure_opening_score, call_structure_info_gathering_score: eval.call_structure_info_gathering_score, call_structure_solution_presentation_score: eval.call_structure_solution_presentation_score, call_structure_closing_score: eval.call_structure_closing_score, id: eval.id } } ELSE null END ) as callEvaluations
        WITH c, entity, collect(r.text_snippet) as snippets, [evl IN callEvaluations WHERE evl IS NOT NULL] as finalEvaluations
        ORDER BY c.date DESC LIMIT 20
        RETURN c.id as id, c.date as date, c.record_type as type, c.title as title, c.primary_staff as staff, c.summary as summary, finalEvaluations as callEvaluations, snippets
        ORDER BY c.date DESC`,
  getIssueDetails: `
        MATCH (iss:Issue {id: $entityId, client_id: $clientId})
        RETURN iss.id as id, iss.name as name, iss.description as description, iss.status as status, iss.severity as severity
        LIMIT 1
    `,
  getIssueBlockedInitiatives: ` 
        MATCH (iss:Issue {id: $entityId, client_id: $clientId})-[:BLOCKS]->(i:Initiative {client_id: $clientId})
        RETURN i.id as id, i.name as name, i.status as status
        ORDER BY i.name
    `,
  getIssueMentionTrend: '/* Uses getEntityMentionTrend logic */\n' + `
        MATCH (entity:Issue {id: $entityId, client_id: $clientId})
        MATCH (c:Communication {client_id: $clientId})-[:EVIDENCED_BY]->(entity)
        WITH date.truncate('week', c.date) AS wk, count(c) AS mentions
        RETURN wk AS weekBeginning, mentions ORDER BY wk ASC`,
  getIssueSentimentTrend: '/* Uses getEntitySentimentTrend logic */\n' + `
        MATCH (entity:Issue {id: $entityId, client_id: $clientId})
        MATCH (c:Communication {client_id: $clientId})-[r:EVIDENCED_BY]->(entity)
        WITH date.truncate('week', c.date) AS wk, avg(r.sentiment_score) AS weeklySentiment
        RETURN wk, round(weeklySentiment, 3) AS avgSentiment ORDER BY wk ASC`,
  getIssueLinkedComms: '/* Uses getEntityLinkedComms logic */\n' + `
        MATCH (entity:Issue {id: $entityId, client_id: $clientId})<-[r:EVIDENCED_BY]-(c:Communication {client_id: $clientId})
        OPTIONAL MATCH (eval:CallEval)-[:EVALUATES]->(c) OPTIONAL MATCH (s_eval:Staff {id: eval.staff_id})
        WITH c, r, entity, collect(DISTINCT CASE WHEN eval IS NOT NULL THEN { staffName: s_eval.name, staffId: eval.staff_id, scores: { overall_score: eval.overall_score, scarf_status_score: eval.scarf_status_score, scarf_certainty_score: eval.scarf_certainty_score, scarf_autonomy_score: eval.scarf_autonomy_score, scarf_relatedness_score: eval.scarf_relatedness_score, scarf_fairness_score: eval.scarf_fairness_score, empathy_score: eval.empathy_score, assertiveness_score: eval.assertiveness_score, respect_score: eval.respect_score, clarity_score: eval.clarity_score, call_structure_opening_score: eval.call_structure_opening_score, call_structure_info_gathering_score: eval.call_structure_info_gathering_score, call_structure_solution_presentation_score: eval.call_structure_solution_presentation_score, call_structure_closing_score: eval.call_structure_closing_score, id: eval.id } } ELSE null END ) as callEvaluations
        WITH c, entity, collect(r.text_snippet) as snippets, [evl IN callEvaluations WHERE evl IS NOT NULL] as finalEvaluations
        ORDER BY c.date DESC LIMIT 20
        RETURN c.id as id, c.date as date, c.record_type as type, c.title as title, c.primary_staff as staff, c.summary as summary, finalEvaluations as callEvaluations, snippets
        ORDER BY c.date DESC`,
  getActionItemDetails: `
         MATCH (a:ActionItem {id: $entityId, client_id: $clientId})
         RETURN a.id as id, a.name as name, a.description as description, a.status as status, a.assigned_to as assignee, a.deadline as deadline
         LIMIT 1
     `,
  getActionItemSupportedInitiative: `
         MATCH (a:ActionItem {id: $entityId, client_id: $clientId})-[:SUPPORTS]->(i:Initiative {client_id: $clientId})
         RETURN i.id as id, i.name as name, i.status as status
         LIMIT 1 
     `,
  getActionItemMentionTrend: '/* Uses getEntityMentionTrend logic */\n' + `
        MATCH (entity:ActionItem {id: $entityId, client_id: $clientId})
        MATCH (c:Communication {client_id: $clientId})-[:EVIDENCED_BY]->(entity)
        WITH date.truncate('week', c.date) AS wk, count(c) AS mentions
        RETURN wk AS weekBeginning, mentions ORDER BY wk ASC`,
  getActionItemSentimentTrend: '/* Uses getEntitySentimentTrend logic */\n' + `
        MATCH (entity:ActionItem {id: $entityId, client_id: $clientId})
        MATCH (c:Communication {client_id: $clientId})-[r:EVIDENCED_BY]->(entity)
        WITH date.truncate('week', c.date) AS wk, avg(r.sentiment_score) AS weeklySentiment
        RETURN wk, round(weeklySentiment, 3) AS avgSentiment ORDER BY wk ASC`,
  getActionItemLinkedComms: '/* Uses getEntityLinkedComms logic */\n' + `
        MATCH (entity:ActionItem {id: $entityId, client_id: $clientId})<-[r:EVIDENCED_BY]-(c:Communication {client_id: $clientId})
        OPTIONAL MATCH (eval:CallEval)-[:EVALUATES]->(c) OPTIONAL MATCH (s_eval:Staff {id: eval.staff_id})
        WITH c, r, entity, collect(DISTINCT CASE WHEN eval IS NOT NULL THEN { staffName: s_eval.name, staffId: eval.staff_id, scores: { overall_score: eval.overall_score, scarf_status_score: eval.scarf_status_score, scarf_certainty_score: eval.scarf_certainty_score, scarf_autonomy_score: eval.scarf_autonomy_score, scarf_relatedness_score: eval.scarf_relatedness_score, scarf_fairness_score: eval.scarf_fairness_score, empathy_score: eval.empathy_score, assertiveness_score: eval.assertiveness_score, respect_score: eval.respect_score, clarity_score: eval.clarity_score, call_structure_opening_score: eval.call_structure_opening_score, call_structure_info_gathering_score: eval.call_structure_info_gathering_score, call_structure_solution_presentation_score: eval.call_structure_solution_presentation_score, call_structure_closing_score: eval.call_structure_closing_score, id: eval.id } } ELSE null END ) as callEvaluations
        WITH c, entity, collect(r.text_snippet) as snippets, [evl IN callEvaluations WHERE evl IS NOT NULL] as finalEvaluations
        ORDER BY c.date DESC LIMIT 20
        RETURN c.id as id, c.date as date, c.record_type as type, c.title as title, c.primary_staff as staff, c.summary as summary, finalEvaluations as callEvaluations, snippets
        ORDER BY c.date DESC`,
};

export const updateQueries = {
  updateNodeProperty: `
    MATCH (n {id: $nodeId})
    CALL apoc.create.setProperty(n, $propertyName, $newValue)
    YIELD node
    RETURN node 
  `,
  updateNodeStatus: `
    MATCH (n {id: $nodeId}) 
    SET n.status = $newValue
    RETURN n.status as updatedStatus
  `,
  updateNodeSeverity: `
    MATCH (n:Issue {id: $nodeId})
    SET n.severity = $newValue
    RETURN n.severity as updatedSeverity
  `,
};

export const staffQueries = {
  getDistinctDRIsForClient: `
    MATCH (a:ActionItem {client_id: $clientId})
    WHERE a.assigned_to IS NOT NULL AND a.assigned_to <> ''
    RETURN DISTINCT a.assigned_to as dri
    ORDER BY dri ASC
  `,
  getAllStaff: `
    MATCH (s:Staff)
    RETURN s.id as staffId, s.name as staffName, s.image_url as staffImageUrl 
    ORDER BY s.name ASC
  `,

  getStaffPerformanceSummaryList: `
    MATCH (s:Staff)
    OPTIONAL MATCH (eval_recent:CallEval {staff_id: s.id})
    WHERE eval_recent.created_at >= datetime() - duration({days: 14}) AND eval_recent.overall_score IS NOT NULL
    WITH s, avg(eval_recent.overall_score) AS trailingTwoWeekScore
    
    OPTIONAL MATCH (eval_trend:CallEval {staff_id: s.id})
    WHERE eval_trend.created_at >= datetime() - duration({weeks: 8}) AND eval_trend.overall_score IS NOT NULL AND eval_trend.created_at IS NOT NULL
    WITH s, trailingTwoWeekScore, 
         CASE WHEN eval_trend.created_at IS NOT NULL THEN date.truncate('week', eval_trend.created_at) ELSE null END AS trendWeek, 
         avg(eval_trend.overall_score) AS trendWeeklyAvgScore
    ORDER BY s.id, trendWeek ASC 
    WITH s, trailingTwoWeekScore, collect(CASE WHEN trendWeek IS NOT NULL THEN {week: trendWeek, score: round(trendWeeklyAvgScore, 2)} ELSE null END) AS weeklyTrendData
    
    RETURN s.id AS staffId,
           s.name AS staffName,
           s.image_url AS staffImageUrl, 
           round(trailingTwoWeekScore, 2) AS trailingScore,
           [item IN weeklyTrendData WHERE item IS NOT NULL AND item.week IS NOT NULL] AS weeklyScores
    ORDER BY s.name ASC
  `,
  getCallEvalsForStaff: `
    MATCH (s:Staff {id: $staffId})
    MATCH (eval:CallEval {staff_id: s.id})-[:EVALUATES]->(c:Communication)
    WHERE ($clientId IS NULL OR c.client_id = $clientId)
    ORDER BY c.date ASC, eval.created_at ASC
    RETURN properties(eval) as evaluationData, c.date as communicationDate, c.client_id as clientId 
  `,
  getLatestStaffEval: `
    MATCH (s:Staff {id: $staffId})<-[:GENERATED_FOR]-(se:StaffEval)
    WHERE ($clientId IS NOT NULL AND se.client_id = $clientId) 
       OR ($clientId IS NULL AND se.client_id IS NULL)       
    WITH s, se
    ORDER BY se.report_generated_at DESC
    RETURN properties(se) as staffEvalReport
    LIMIT 1
  `,
  getStaffKeyMetrics: `
    MATCH (s:Staff {id: $staffId})
    
    OPTIONAL MATCH (eval:CallEval {staff_id: s.id})-[:EVALUATES]->(comm_eval:Communication)
    WHERE eval.overall_score IS NOT NULL AND ($clientId IS NULL OR comm_eval.client_id = $clientId)
    WITH s, round(avg(eval.overall_score), 2) AS avgOverallCallScore
    
    OPTIONAL MATCH (c:Communication)
    WHERE c.primary_staff IN s.emails AND ($clientId IS NULL OR c.client_id = $clientId)
    WITH s, avgOverallCallScore, count(DISTINCT c) AS totalCommunications
    
    OPTIONAL MATCH (ai_pending:ActionItem {assigned_to: s.name, status: 'open'}) 
    WHERE ($clientId IS NULL OR ai_pending.client_id = $clientId) 
    WITH s, avgOverallCallScore, totalCommunications, count(DISTINCT ai_pending) AS pendingActionItems
    
    RETURN s.name as staffName,
           avgOverallCallScore,
           totalCommunications, 
           pendingActionItems
  `,
  getStaffCommunicationFrequency: `
    MATCH (s:Staff {id: $staffId})
    MATCH (c:Communication)
    WHERE c.primary_staff IN s.emails AND c.client_id IS NOT NULL AND ($clientId IS NULL OR c.client_id = $clientId)
    WITH date.truncate('week', c.date) AS weekBeginning,
         coalesce(c.record_type, 'Unknown') AS commType,
         c.client_id AS forClientId, 
         count(c) AS weeklyCount
    ORDER BY weekBeginning ASC, forClientId ASC, commType ASC
    RETURN weekBeginning, forClientId, commType, weeklyCount
  `,
  getStaffCommunicationTypes: `
    MATCH (s:Staff {id: $staffId})
    MATCH (c:Communication) 
    WHERE c.primary_staff IN s.emails
    RETURN DISTINCT coalesce(c.record_type, 'Unknown') AS commType
    ORDER BY commType ASC
  `,
  getStaffAveragePerformanceScores: `
    MATCH (s:Staff {id: $staffId})
    MATCH (eval:CallEval {staff_id: s.id})-[:EVALUATES]->(comm:Communication)
    WHERE ($clientId IS NULL OR comm.client_id = $clientId) AND eval.overall_score IS NOT NULL

    WITH s, comm, eval,
        datetime() as today
    OPTIONAL MATCH (eval_current:CallEval {staff_id: s.id})-[:EVALUATES]->(comm_current:Communication)
    WHERE ($clientId IS NULL OR comm_current.client_id = $clientId)
      AND comm_current.date >= today - duration({days: 30})
      AND eval_current.overall_score IS NOT NULL

    OPTIONAL MATCH (eval_trailing:CallEval {staff_id: s.id})-[:EVALUATES]->(comm_trailing:Communication)
    WHERE ($clientId IS NULL OR comm_trailing.client_id = $clientId)
      AND comm_trailing.date >= today - duration({days: 90}) 
      AND comm_trailing.date < today - duration({days: 30})  
      AND eval_trailing.overall_score IS NOT NULL
    
    RETURN
        round(avg(eval_current.scarf_status_score), 2) AS current_avg_scarf_status,
        round(avg(eval_current.scarf_certainty_score), 2) AS current_avg_scarf_certainty,
        round(avg(eval_current.scarf_autonomy_score), 2) AS current_avg_scarf_autonomy,
        round(avg(eval_current.scarf_relatedness_score), 2) AS current_avg_scarf_relatedness,
        round(avg(eval_current.scarf_fairness_score), 2) AS current_avg_scarf_fairness,
        round(avg(eval_current.empathy_score), 2) AS current_avg_comm_empathy,
        round(avg(eval_current.assertiveness_score), 2) AS current_avg_comm_assertiveness,
        round(avg(eval_current.respect_score), 2) AS current_avg_comm_respect,
        round(avg(eval_current.clarity_score), 2) AS current_avg_comm_clarity,
        round(avg(eval_current.call_structure_opening_score), 2) AS current_avg_struct_opening,
        round(avg(eval_current.call_structure_info_gathering_score), 2) AS current_avg_struct_info_gathering,
        round(avg(eval_current.call_structure_solution_presentation_score), 2) AS current_avg_struct_solution,
        round(avg(eval_current.call_structure_closing_score), 2) AS current_avg_struct_closing,
        round(avg(eval_current.overall_score), 2) AS current_avg_overall_score,

        round(avg(eval_trailing.scarf_status_score), 2) AS trailing_avg_scarf_status,
        round(avg(eval_trailing.scarf_certainty_score), 2) AS trailing_avg_scarf_certainty,
        round(avg(eval_trailing.scarf_autonomy_score), 2) AS trailing_avg_scarf_autonomy,
        round(avg(eval_trailing.scarf_relatedness_score), 2) AS trailing_avg_scarf_relatedness,
        round(avg(eval_trailing.scarf_fairness_score), 2) AS trailing_avg_scarf_fairness,
        round(avg(eval_trailing.empathy_score), 2) AS trailing_avg_comm_empathy,
        round(avg(eval_trailing.assertiveness_score), 2) AS trailing_avg_comm_assertiveness,
        round(avg(eval_trailing.respect_score), 2) AS trailing_avg_comm_respect,
        round(avg(eval_trailing.clarity_score), 2) AS trailing_avg_comm_clarity,
        round(avg(eval_trailing.call_structure_opening_score), 2) AS trailing_avg_struct_opening,
        round(avg(eval_trailing.call_structure_info_gathering_score), 2) AS trailing_avg_struct_info_gathering,
        round(avg(eval_trailing.call_structure_solution_presentation_score), 2) AS trailing_avg_struct_solution,
        round(avg(eval_trailing.call_structure_closing_score), 2) AS trailing_avg_struct_closing,
        round(avg(eval_trailing.overall_score), 2) AS trailing_avg_overall_score
  `,

  getCommunicatedClientsForStaff: `
    MATCH (s:Staff {id: $staffId})-[:PARTICIPATED]->(comm:Communication)
    WHERE comm.client_id IS NOT NULL
    RETURN DISTINCT comm.client_id AS clientId
    ORDER BY clientId ASC
  `
};

// Department-specific queries
export const departmentQueries = {
  getDepartmentDRIs: `
    MATCH (s:Staff {department: $departmentId})
    WHERE s.name IS NOT NULL
    RETURN DISTINCT s.name AS dri
    ORDER BY dri
  `,
  listAllDepartments: `
    MATCH (s:Staff)
    WHERE s.department IS NOT NULL
    RETURN DISTINCT s.department as department
    ORDER BY department
  `,
  getDepartmentPriorityCounts: `
    MATCH (staff:Staff {department: $department})
    WITH collect(staff.name) as deptStaffNames,
         collect(staff.emails) as deptStaffEmailsNested, // Collect lists of emails
         $startDate AS startDate,
         $endDate AS endDate,
         $department AS targetDept,
         $driFilter as driFilter
    
    // Flatten the list of email lists first
    WITH deptStaffNames, [email IN apoc.coll.flatten(deptStaffEmailsNested) WHERE email IS NOT NULL] as deptStaffEmails,
         startDate, endDate, targetDept, driFilter

    // Now, use the newly created deptStaffEmails in the next WITH clause
    WITH deptStaffNames, deptStaffEmails, startDate, endDate, targetDept,
        CASE WHEN driFilter IS NULL OR size(driFilter) = 0 THEN deptStaffNames ELSE driFilter END as effectiveDRIs,
        CASE WHEN driFilter IS NULL OR size(driFilter) = 0 THEN deptStaffEmails ELSE [s in deptStaffEmails WHERE s STARTS WITH driFilter[0]] END as effectiveDriEmails
        
    // Total Active Goals for Department
    CALL {
        WITH effectiveDriEmails, startDate, endDate
        OPTIONAL MATCH (g:Goal {status: 'open'})
        WHERE EXISTS {
          (gc:Communication)-[:EVIDENCED_BY]->(g)
          WHERE gc.primary_staff IN effectiveDriEmails AND date(gc.date) >= date(startDate) AND date(gc.date) <= date(endDate)
        }
        AND (g.start_date IS NULL OR date(g.start_date) <= date(endDate))
        AND (g.achieved_date IS NULL OR date(g.achieved_date) >= date(startDate))
        RETURN count(DISTINCT g) as totalGoalsCount
    }
    
    // Critical Issues for Department
    CALL {
        WITH effectiveDRIs, effectiveDriEmails, startDate, endDate
        OPTIONAL MATCH (crit_i:Issue {severity: 'high', status: 'open'})
        WHERE (crit_i.assigned_to IN effectiveDRIs OR EXISTS {
                (cic:Communication)-[:EVIDENCED_BY]->(crit_i)
                WHERE cic.primary_staff IN effectiveDriEmails AND date(cic.date) >= date(startDate) AND date(cic.date) <= date(endDate)
              })
        AND (crit_i.created_at IS NULL OR date(crit_i.created_at) <= date(endDate))
        RETURN count(DISTINCT crit_i) as criticalIssuesCount
    }

    // Total Issues for Department (non-resolved)
    CALL {
        WITH effectiveDRIs, effectiveDriEmails, startDate, endDate
        OPTIONAL MATCH (total_i:Issue)
         WHERE total_i.status = 'open'
          AND (total_i.assigned_to IN effectiveDRIs OR EXISTS {
                (tic:Communication)-[:EVIDENCED_BY]->(total_i)
                WHERE tic.primary_staff IN effectiveDriEmails AND date(tic.date) >= date(startDate) AND date(tic.date) <= date(endDate)
              })
          AND (total_i.created_at IS NULL OR date(total_i.created_at) <= date(endDate))
        RETURN count(DISTINCT total_i) as totalIssuesCount
    }

    // Blocked Initiatives for Department
    CALL {
       WITH effectiveDRIs, effectiveDriEmails, startDate, endDate
        OPTIONAL MATCH (init_b:Initiative)<-[:BLOCKS]-(iss_b:Issue)
        WHERE init_b.status = 'open'
          AND iss_b.status = 'open'
          AND (
               EXISTS {
                 (aib:ActionItem)-[:SUPPORTS]->(init_b)
                 WHERE aib.assigned_to IN effectiveDRIs
               }
               OR EXISTS {
                 (cb:Communication)-[:EVIDENCED_BY]->(init_b)
                 WHERE cb.primary_staff IN effectiveDriEmails AND date(cb.date) >= date(startDate) AND date(cb.date) <= date(endDate)
               }
              )
          AND (init_b.start_date IS NULL OR date(init_b.start_date) <= date(endDate))
        RETURN count(DISTINCT init_b) as blockedInitiativesCount
    }

    // Total Initiatives for Department (non-completed/cancelled)
    CALL {
       WITH effectiveDRIs, effectiveDriEmails, startDate, endDate
        OPTIONAL MATCH (total_init:Initiative)
        WHERE total_init.status = 'open'
          AND (
               EXISTS {
                 (aiti:ActionItem)-[:SUPPORTS]->(total_init)
                 WHERE aiti.assigned_to IN effectiveDRIs
               }
               OR EXISTS {
                 (cti:Communication)-[:EVIDENCED_BY]->(total_init)
                 WHERE cti.primary_staff IN effectiveDriEmails AND date(cti.date) >= date(startDate) AND date(cti.date) <= date(endDate)
               }
              )
          AND (total_init.start_date IS NULL OR date(total_init.start_date) <= date(endDate))
        RETURN count(DISTINCT total_init) as totalInitiativesCount
    }

    // Overdue Actions for Department
    CALL {
        WITH effectiveDRIs, endDate
        OPTIONAL MATCH (a_od:ActionItem {status: 'open'})
        WHERE a_od.assigned_to IN effectiveDRIs
          AND a_od.deadline IS NOT NULL AND date(a_od.deadline) < date(endDate)
          AND (a_od.created_at IS NULL OR date(a_od.created_at) <= date(endDate))
        RETURN count(DISTINCT a_od) as overdueActionsCount
    }
    
    // Neglected Goals for Department
    CALL {
        WITH targetDept, startDate, endDate
        OPTIONAL MATCH (g_neg:Goal {status: 'open'})
        WHERE EXISTS {
            (staff_neg:Staff {department: targetDept})-[:PARTICIPATED|PRIMARY_CONTACT_FOR]->(comm_neg_client:Communication {client_id: g_neg.client_id})
            WHERE date(comm_neg_client.date) >= date(startDate) AND date(comm_neg_client.date) <= date(endDate)
        }
        AND (g_neg.start_date IS NULL OR date(g_neg.start_date) <= date(endDate))
        AND (g_neg.achieved_date IS NULL OR date(g_neg.achieved_date) >= date(startDate))
        AND NOT EXISTS {
          MATCH (c_neg_check:Communication)-[:EVIDENCED_BY]->(g_neg)
          WHERE date(c_neg_check.date) >= date(endDate) - duration({days:14}) AND date(c_neg_check.date) <= date(endDate)
            AND EXISTS {(s_check:Staff {department: targetDept})-[:PARTICIPATED|PRIMARY_CONTACT_FOR]->(c_neg_check)}
        }
        AND NOT EXISTS {
          MATCH (g_neg)<-[:SUPPORTS_GOAL]-(i_neg_check:Initiative)
          MATCH (c2_neg_check:Communication)-[:EVIDENCED_BY]->(i_neg_check)
          WHERE date(c2_neg_check.date) >= date(endDate) - duration({days:14}) AND date(c2_neg_check.date) <= date(endDate)
            AND EXISTS {(s_check2:Staff {department: targetDept})-[:PARTICIPATED|PRIMARY_CONTACT_FOR]->(c2_neg_check)}
        }
        RETURN count(DISTINCT g_neg) as neglectedGoalsCount
    }

    RETURN criticalIssuesCount, totalIssuesCount, blockedInitiativesCount, totalInitiativesCount, overdueActionsCount, neglectedGoalsCount, totalGoalsCount
  `,
  getDepartmentEisenhowerActionItems: `
    MATCH (staff:Staff {department: $department})
    WITH collect(staff.name) as deptStaffNames, $startDate AS startDate, $endDate AS endDate
    MATCH (a:ActionItem {status: 'open'})
    WHERE a.assigned_to IN deptStaffNames
      AND (a.created_at IS NULL OR date(a.created_at) <= date(endDate))
      AND (a.resolved_at IS NULL OR date(a.resolved_at) >= date(startDate))
    WITH a, startDate, endDate,
      CASE
        WHEN a.deadline IS NULL THEN 0.3
        WHEN date(a.deadline) > date(endDate) THEN 0.3
        WHEN date(a.deadline) < date(startDate) THEN 1.0
        WHEN date(a.deadline) >= date(startDate) AND date(a.deadline) <= date(endDate) AND duration.between(date(startDate), date(a.deadline)).days <= 7 THEN 0.8
        WHEN date(a.deadline) >= date(startDate) AND date(a.deadline) <= date(endDate) AND duration.between(date(startDate), date(a.deadline)).days <= 14 THEN 0.6
        ELSE 0.3
      END AS urgencyScore
    OPTIONAL MATCH (c:Communication)-[:EVIDENCED_BY]->(a)
    WHERE date(c.date) >= date(startDate) AND date(c.date) <= date(endDate)
    WITH a, urgencyScore, startDate, endDate, count(DISTINCT c) AS recentMentions
    OPTIONAL MATCH (a)-[:SUPPORTS]->(i:Initiative)
    OPTIONAL MATCH (iss:Issue)-[:BLOCKS]->(i)
    WHERE iss.status = 'open' AND iss.severity = 'high'
    WITH a, urgencyScore, recentMentions, count(DISTINCT iss) AS criticalBlockers
    WITH a, urgencyScore,
      CASE
        WHEN criticalBlockers > 0 THEN 0.9
        WHEN recentMentions >= 5 THEN 0.8
        WHEN recentMentions >= 2 THEN 0.6
        ELSE 0.3
      END AS importanceScore
    RETURN a.id as id, a.name as name, a.assigned_to as assignee, a.deadline as deadline,
           a.client_id as clientId, labels(a)[0] as type, urgencyScore, importanceScore,
           a.status as detail1, a.deadline as detail2,
           CASE
             WHEN urgencyScore >= 0.6 AND importanceScore >= 0.6 THEN 'do'
             WHEN urgencyScore < 0.6 AND importanceScore >= 0.6 THEN 'schedule'
             WHEN urgencyScore >= 0.6 AND importanceScore < 0.6 THEN 'delegate'
             ELSE 'eliminate'
           END AS quadrant
    ORDER BY urgencyScore DESC, importanceScore DESC
  `,
  getDepartmentStaffWorkload: `
    MATCH (staff:Staff {department: $department})
    WHERE $driFilter IS NULL OR size($driFilter) = 0 OR ANY(filterDri IN $driFilter WHERE staff.name CONTAINS filterDri)
    WITH staff, $startDate AS startDate, $endDate AS endDate
    OPTIONAL MATCH (a:ActionItem {assigned_to: staff.name, status: 'open'})
    WHERE (a.created_at IS NULL OR date(a.created_at) <= date(endDate))
      AND (a.resolved_at IS NULL OR date(a.resolved_at) >= date(startDate))
    RETURN staff.name as staffName,
           count(DISTINCT a) as totalTasks,
           count(DISTINCT CASE WHEN a.deadline < date(endDate) THEN a END) as overdueTasks,
           count(DISTINCT CASE WHEN a.deadline >= date(startDate) AND a.deadline <= date.truncate('week', date(startDate)) + duration({days: 7}) THEN a END) as dueSoonTasks,
           count(DISTINCT a.client_id) as clientCount
    ORDER BY totalTasks DESC
  `,
  getDepartmentClientHealth: `
    MATCH (staff:Staff {department: $department})
    WITH collect(staff.name) as deptStaffNames,
        collect(staff.emails) as deptStaffEmailsNested, // Assuming staff.emails is a list
        $startDate AS startDate,
        $endDate AS endDate
    WITH deptStaffNames, [email in apoc.coll.flatten(deptStaffEmailsNested) WHERE email IS NOT NULL] as deptStaffEmails,
        startDate, endDate
    MATCH (n) WHERE n.client_id IS NOT NULL
    AND (
      (n:ActionItem AND n.assigned_to IN deptStaffNames AND (n.created_at IS NULL OR date(n.created_at) <= date(endDate)) AND (n.resolved_at IS NULL OR date(n.resolved_at) >= date(startDate)))
      OR EXISTS {
        MATCH (c:Communication {client_id: n.client_id})
        WHERE c.primary_staff IN deptStaffEmails AND date(c.date) >= date(startDate) AND date(c.date) <= date(endDate)
      }
    )
    WITH DISTINCT n.client_id AS clientId, deptStaffNames, startDate, endDate
    OPTIONAL MATCH (g:Goal {client_id: clientId, status: 'open'})
    WHERE (g.start_date IS NULL OR date(g.start_date) <= date(endDate)) AND (g.achieved_date IS NULL OR date(g.achieved_date) >= date(startDate))
    WITH clientId, deptStaffNames, startDate, endDate, count(DISTINCT g) AS activeGoals
    OPTIONAL MATCH (i:Issue {client_id: clientId})
    WHERE i.status = 'open' AND i.severity = 'high' AND (i.created_at IS NULL OR date(i.created_at) <= date(endDate)) AND (i.resolved_at IS NULL OR date(i.resolved_at) >= date(startDate))
    WITH clientId, deptStaffNames, startDate, endDate, activeGoals, count(DISTINCT i) AS highSeverityIssues
    OPTIONAL MATCH (init:Initiative {client_id: clientId})<-[:BLOCKS]-(blocker:Issue)
    WHERE init.status = 'open' AND blocker.status = 'open' AND (init.start_date IS NULL OR date(init.start_date) <= date(endDate))
    WITH clientId, deptStaffNames, startDate, endDate, activeGoals, highSeverityIssues, count(DISTINCT init) AS blockedInitiatives
    OPTIONAL MATCH (a:ActionItem {client_id: clientId, status: 'open'})
    WHERE a.deadline < date(endDate) AND a.assigned_to IN deptStaffNames AND (a.created_at IS NULL OR date(a.created_at) <= date(endDate))
    WITH clientId, activeGoals, highSeverityIssues, blockedInitiatives, count(DISTINCT a) AS overdueActions
    WITH clientId, activeGoals, highSeverityIssues, blockedInitiatives, overdueActions,
      CASE
        WHEN activeGoals = 0 THEN 50
        ELSE 100 - (highSeverityIssues * 10) - (blockedInitiatives * 5) - (overdueActions * 2)
      END AS healthScore
    RETURN clientId, activeGoals, highSeverityIssues, blockedInitiatives, overdueActions,
          CASE WHEN healthScore < 0 THEN 0 WHEN healthScore > 100 THEN 100 ELSE healthScore END AS healthScore
    ORDER BY healthScore ASC
  `,
  getDepartmentRecentPriorityCommunications: `
    MATCH (staff:Staff {department: $department})
    WHERE $driFilter IS NULL OR size($driFilter) = 0 OR ANY(filterDri IN $driFilter WHERE staff.name CONTAINS filterDri)
    WITH collect(staff.emails) as deptStaffEmailsNested, $startDate AS startDate, $endDate AS endDate // Assuming staff.emails
    WITH [email IN apoc.coll.flatten(deptStaffEmailsNested) WHERE email IS NOT NULL] as deptStaffEmails, startDate, endDate
    MATCH (c:Communication)
    WHERE c.primary_staff IN deptStaffEmails AND date(c.date) >= date(startDate) AND date(c.date) <= date(endDate)
    OPTIONAL MATCH (c)-[:EVIDENCED_BY]->(iss:Issue {severity: 'high'}) WHERE iss.status = 'open'
    OPTIONAL MATCH (c)-[:EVIDENCED_BY]->(init:Initiative)<-[:BLOCKS]-(blocker:Issue) WHERE init.status = 'open' AND blocker.status = 'open'
    WITH c, count(DISTINCT iss) AS highSeverityIssues, count(DISTINCT init) AS blockedInitiatives
    WHERE highSeverityIssues > 0 OR blockedInitiatives > 0
    RETURN c.id as id, c.client_id as clientId, c.date as date, c.record_type as type,
           c.title as title, c.summary as summary, c.primary_staff as staff,
           highSeverityIssues, blockedInitiatives
    ORDER BY c.date DESC LIMIT 20
  `,
  getDepartmentGoalsList: `
    MATCH (staff_member:Staff {department: $departmentId})
    WITH staff_member, $startDate AS startDate, $endDate AS endDate
    MATCH (g:Goal)
    WHERE (
        EXISTS { 
            (comm_g:Communication {client_id: g.client_id})
            WHERE (comm_g.primary_staff IN staff_member.emails OR staff_member.name IN comm_g.participants)
                AND date(comm_g.date) >= date(startDate) AND date(comm_g.date) <= date(endDate)
                AND ( (comm_g)-[:EVIDENCED_BY]->(g) OR EXISTS{ (comm_g)-[:EVIDENCED_BY]->(:Initiative)-[:SUPPORTS_GOAL]->(g) } )
        }
        OR EXISTS { (staff_member)-[:ASSIGNED_TO|REPORTER_OF]->(g) }
    )
    AND (g.start_date IS NULL OR date(g.start_date) <= date($endDate))
    AND (g.status = 'open' OR (g.achieved_date IS NOT NULL AND date(g.achieved_date) >= date($startDate)))
    
    OPTIONAL MATCH (i:Initiative)-[:SUPPORTS_GOAL]->(g)
    OPTIONAL MATCH (iss:Issue)-[:BLOCKS]->(i) WHERE iss.status = 'open'
    WITH g, staff_member, startDate, endDate, count(DISTINCT i) AS initiativeCount, count(DISTINCT iss) AS blockingIssuesCount

    CALL {
        WITH g
        OPTIONAL MATCH (g)<-[:SUPPORTS_GOAL]-(:Initiative)<-[:SUPPORTS]-(ai:ActionItem)
        WHERE ai.assigned_to IS NOT NULL
        WITH g, collect({person: ai.assigned_to, score: 2}) as actionItemScores
        OPTIONAL MATCH (comm:Communication)-[:EVIDENCED_BY]->(g)
        WHERE comm.primary_staff IS NOT NULL
        MATCH (s:Staff) WHERE comm.primary_staff IN s.emails
        WITH g, actionItemScores, collect({person: s.name, score: 1}) as commScores
        UNWIND(actionItemScores + commScores) as scoreData
        WITH scoreData.person as person, sum(scoreData.score) as totalScore
        WHERE person IS NOT NULL
        ORDER BY totalScore DESC
        RETURN collect(person)[0] as calculatedDri
    }
    
    WITH g, COALESCE(g.assigned_to, calculatedDri) as dri, initiativeCount, blockingIssuesCount, staff_member, startDate, endDate
    WHERE ($driFilter IS NULL OR size($driFilter) = 0 OR ANY(filterDri IN $driFilter WHERE dri CONTAINS filterDri))

    OPTIONAL MATCH (g)<-[:EVIDENCED_BY]-(comm_count:Communication)
    WHERE (comm_count.primary_staff IN staff_member.emails OR staff_member.name IN comm_count.participants)
        AND date(comm_count.date) >= date(startDate) AND date(comm_count.date) <= date(endDate)
    
    WITH g, dri, initiativeCount, blockingIssuesCount, count(comm_count) AS commCount
    RETURN g.id as id, g.name as name, g.description as description, g.status as status,
           g.target_date as targetDate,
           initiativeCount,
           blockingIssuesCount,
           coalesce(size([(g)-[]-(related) WHERE labels(related)[0] IN ['Initiative', 'Issue', 'ActionItem'] | 1]), 0) + commCount AS score,
           g.client_id as clientId,
           dri as assigned_to
    ORDER BY score DESC, g.target_date ASC
  `,
  getDepartmentInitiativesList: `
    MATCH (staff_member:Staff {department: $departmentId})
    WITH staff_member, $startDate AS startDate, $endDate AS endDate
    MATCH (ini:Initiative)
    WHERE (
        EXISTS {
            (comm_ini:Communication {client_id: ini.client_id})
            WHERE (comm_ini.primary_staff IN staff_member.emails OR staff_member.name IN ini.participants)
              AND date(comm_ini.date) >= date(startDate) AND date(comm_ini.date) <= date(endDate)
              AND ( (comm_ini)-[:EVIDENCED_BY]->(ini) OR EXISTS{ (ai:ActionItem {assigned_to: staff_member.name})-[:SUPPORTS]->(ini) } )
        }
         OR EXISTS { (ai_direct:ActionItem {assigned_to: staff_member.name})-[:SUPPORTS]->(ini) }
    )
    AND (ini.start_date IS NULL OR date(ini.start_date) <= date($endDate))
    AND (ini.status = 'open' OR (ini.completed_date IS NOT NULL AND date(ini.completed_date) >= date($startDate)))

    OPTIONAL MATCH (ini)-[:SUPPORTS_GOAL]->(g_sup:Goal)
    OPTIONAL MATCH (iss_block:Issue)-[:BLOCKS]->(ini) WHERE iss_block.status = 'open'
    OPTIONAL MATCH (ai_sup:ActionItem)-[:SUPPORTS]->(ini)
    WITH ini, staff_member, startDate, endDate, collect(DISTINCT g_sup.name) as supportedGoals, count(DISTINCT iss_block) as blockingIssuesCount, count(DISTINCT ai_sup) as actionItemCount
    
    CALL {
        WITH ini
        OPTIONAL MATCH (ini)<-[:SUPPORTS]-(ai:ActionItem)
        WHERE ai.assigned_to IS NOT NULL
        WITH ini, collect({person: ai.assigned_to, score: 2}) as actionItemScores
        OPTIONAL MATCH (comm:Communication)-[:EVIDENCED_BY]->(ini)
        WHERE comm.primary_staff IS NOT NULL
        MATCH (s:Staff) WHERE comm.primary_staff IN s.emails
        WITH ini, actionItemScores, collect({person: s.name, score: 1}) as commScores
        UNWIND(actionItemScores + commScores) as scoreData
        WITH scoreData.person as person, sum(scoreData.score) as totalScore
        WHERE person IS NOT NULL
        ORDER BY totalScore DESC
        RETURN collect(person)[0] as calculatedDri
    }
    
    WITH ini, COALESCE(ini.assigned_to, calculatedDri) as dri, supportedGoals, blockingIssuesCount, actionItemCount, staff_member, startDate, endDate
    WHERE ($driFilter IS NULL OR size($driFilter) = 0 OR ANY(filterDri IN $driFilter WHERE dri CONTAINS filterDri))

    OPTIONAL MATCH (ini_comm_count:Communication)-[:EVIDENCED_BY]->(ini)
    WHERE (ini_comm_count.primary_staff IN staff_member.emails OR staff_member.name IN ini_comm_count.participants)
      AND date(ini_comm_count.date) >= date(startDate) AND date(ini_comm_count.date) <= date(endDate)
    
    WITH ini, dri, supportedGoals, blockingIssuesCount, actionItemCount, count(ini_comm_count) AS commCountForInitiative
    RETURN ini.id as id, ini.name as name, ini.description as description, ini.status as status,
           supportedGoals,
           blockingIssuesCount,
           actionItemCount,
           coalesce(size([(ini)-[]-(related) WHERE labels(related)[0] IN ['Goal', 'Issue', 'ActionItem'] | 1]), 0) + commCountForInitiative AS score,
           ini.client_id as clientId,
           dri as assigned_to
    ORDER BY score DESC, ini.name ASC
  `,
  getDepartmentActionItemsList: `
    MATCH (s:Staff {department: $departmentId})
    WITH s.name AS staffName, $startDate AS startDate, $endDate AS endDate, $assigneeFilter AS assigneeFilter, $initiativeFilter AS initiativeFilter, $driFilter as driFilter
    MATCH (a:ActionItem)
    WHERE a.assigned_to = staffName
      AND (assigneeFilter IS NULL OR assigneeFilter = '' OR a.assigned_to = assigneeFilter)
      AND (driFilter IS NULL OR size($driFilter) = 0 OR ANY(filterDri IN $driFilter WHERE a.assigned_to CONTAINS filterDri))
      AND (a.created_at IS NULL OR date(a.created_at) <= date(endDate))
      AND (a.status = 'open' OR (a.resolved_at IS NOT NULL AND date(a.resolved_at) >= date(startDate)))
    OPTIONAL MATCH (a)-[:SUPPORTS]->(i:Initiative)
    WITH a, i, startDate, endDate, initiativeFilter
    WHERE (initiativeFilter IS NULL OR initiativeFilter = '' OR i.name = initiativeFilter)
    WITH DISTINCT a, startDate, endDate
    OPTIONAL MATCH (a)-[:SUPPORTS]->(init_sup:Initiative)
    WITH a, collect(DISTINCT init_sup.name) as initiativeNames, startDate, endDate
    OPTIONAL MATCH (comm_count_a:Communication)-[:EVIDENCED_BY]->(a)
    WHERE date(comm_count_a.date) >= date(startDate) AND date(comm_count_a.date) <= date(endDate)
    WITH a, initiativeNames, count(comm_count_a) AS commCountForActionItem
    RETURN a.id as id, a.name as name, a.description as description,
           a.assigned_to as assigned_to, a.deadline as deadline, a.status as status,
           initiativeNames,
           coalesce(size([(a)-[]-(related) WHERE labels(related)[0] IN ['Initiative', 'Issue'] | 1]), 0) + commCountForActionItem AS score,
           a.client_id as clientId
    ORDER BY score DESC, a.deadline ASC, a.name ASC
  `,
  getDepartmentCommunicationsList: `
    MATCH (s:Staff {department: $departmentId})
    WITH s, $startDate AS startDate, $endDate AS endDate
    MATCH (c:Communication)
    // Ensure communication is linked to the staff member directly or via a client they are associated with
    WHERE (c.primary_staff IN s.emails OR s.name IN c.participants OR EXISTS {
        (s)-[:PRIMARY_CONTACT_FOR|PARTICIPATED]->(:Communication {client_id: c.client_id})
    })
      AND date(c.date) >= date(startDate) AND date(c.date) <= date(endDate)
    OPTIONAL MATCH (eval:CallEval)-[:EVALUATES]->(c)
    OPTIONAL MATCH (s_eval:Staff {id: eval.staff_id})
    WITH c, s, startDate, endDate, collect(DISTINCT
      CASE WHEN eval IS NOT NULL THEN { staffName: s_eval.name, staffId: eval.staff_id, scores: properties(eval) } ELSE null END
    ) AS callEvaluations
    OPTIONAL MATCH (c)-[r:EVIDENCED_BY]->(n)
    RETURN c.id as id, c.date as date, c.record_type as type, s.name as staff, c.title as title,
           c.summary as summary, c.participants as participants,
           [evl IN callEvaluations WHERE evl IS NOT NULL] as callEvaluations,
           collect(DISTINCT { type: labels(n)[0], name: n.name, sentiment: r.sentiment_score }) as evidencedEntities
    ORDER BY c.date DESC
    LIMIT 100
  `,
  getDepartmentSentimentOverTime: `
    MATCH (s:Staff {department: $departmentId})
    WITH s, $startDate AS startDate, $endDate AS endDate
    MATCH (comm:Communication)
    WHERE (comm.primary_staff IN s.emails OR s.name IN comm.participants OR EXISTS {
        (s)-[:PRIMARY_CONTACT_FOR|PARTICIPATED]->(:Communication {client_id: comm.client_id})
    })
      AND date(comm.date) >= date(startDate) AND date(comm.date) <= date(endDate)
    MATCH (comm)-[r:EVIDENCED_BY]->(n)
    WITH date.truncate('week', comm.date) as weekBeginning, avg(r.sentiment_score) as avgSentiment
    ORDER BY weekBeginning ASC
    RETURN weekBeginning, avgSentiment
  `,
  getDepartmentIssuesList: `
    MATCH (s:Staff {department: $departmentId})
    WITH s, $startDate AS startDate, $endDate AS endDate
    MATCH (iss:Issue)
    WHERE (
      iss.reporter = s.name OR
      EXISTS {
        (comm_iss:Communication)-[:EVIDENCED_BY]->(iss)
        WHERE (comm_iss.primary_staff IN s.emails OR s.name IN comm_iss.participants)
          AND date(comm_iss.date) >= date($startDate) AND date(comm_iss.date) <= date($endDate)
      } OR
      EXISTS {
        (iss)-[:BLOCKS]->(init_blocked:Initiative)
        WHERE (
            EXISTS { (ai_init:ActionItem {assigned_to: s.name})-[:SUPPORTS]->(init_blocked) } OR
            EXISTS {
                (comm_init:Communication)-[:EVIDENCED_BY]->(init_blocked)
                WHERE (comm_init.primary_staff IN s.emails OR s.name IN comm_init.participants)
                    AND date(comm_init.date) >= date($startDate) AND date(comm_init.date) <= date($endDate)
            }
        )
      }
    )
    AND ($severityFilter IS NULL OR $severityFilter = '' OR iss.severity = $severityFilter)
    AND (
      ($statusFilter IS NOT NULL AND $statusFilter <> '' AND iss.status = $statusFilter)
      OR (($statusFilter IS NULL OR $statusFilter = '') AND iss.status = 'open')
    )
    AND (iss.created_at IS NULL OR date(iss.created_at) <= date($endDate))
    AND (iss.status = 'open' OR (iss.resolved_at IS NOT NULL AND date(iss.resolved_at) >= date($startDate)))
    
    CALL {
        WITH iss
        OPTIONAL MATCH (iss)-[:BLOCKS]->(:Initiative)<-[:SUPPORTS]-(ai:ActionItem)
        WHERE ai.assigned_to IS NOT NULL
        WITH iss, collect({person: ai.assigned_to, score: 2}) as actionItemScores
        
        OPTIONAL MATCH (comm:Communication)-[:EVIDENCED_BY]->(iss)
        WHERE comm.primary_staff IS NOT NULL
        // FIX: Renamed 's' to 's_dri' to avoid shadowing
        MATCH (s_dri:Staff) WHERE comm.primary_staff IN s_dri.emails
        WITH iss, actionItemScores, collect({person: s_dri.name, score: 1}) as commScores

        UNWIND(actionItemScores + commScores) as scoreData
        WITH scoreData.person as person, sum(scoreData.score) as totalScore
        WHERE person IS NOT NULL
        ORDER BY totalScore DESC
        RETURN collect(person)[0] as calculatedDri
    }
    
    WITH iss, COALESCE(iss.assigned_to, calculatedDri) as dri
    WHERE $driFilter IS NULL OR size($driFilter) = 0 OR ANY(filterDri IN $driFilter WHERE dri CONTAINS filterDri)
    
    WITH iss, dri, size([(iss)-[]-() | 1]) + size([()-[]-(iss) | 1]) as connectionCount
    OPTIONAL MATCH (iss)-[:BLOCKS]->(init_link:Initiative)
    
    RETURN iss.id as id, iss.name as name, iss.description as description,
          iss.severity as severity, iss.status as status,
          collect(DISTINCT init_link.name) as blockedInitiatives,
          connectionCount AS score,
          iss.client_id as clientId,
          dri as assigned_to
    ORDER BY score DESC, iss.severity, iss.name ASC
  `,
  getDriForIssue: `
    MATCH (iss:Issue {id: $issueId})
    CALL {
        WITH iss
        OPTIONAL MATCH (iss)-[:BLOCKS]->(:Initiative)<-[:SUPPORTS]-(ai:ActionItem)
        WHERE ai.assigned_to IS NOT NULL
        WITH iss, collect({person: ai.assigned_to, score: 2}) as actionItemScores
        
        OPTIONAL MATCH (comm:Communication)-[:EVIDENCED_BY]->(iss)
        WHERE comm.primary_staff IS NOT NULL
        MATCH (s:Staff) WHERE comm.primary_staff IN s.emails
        WITH iss, actionItemScores, collect({person: s.name, score: 1}) as commScores

        UNWIND(actionItemScores + commScores) as scoreData
        WITH scoreData.person as person, sum(scoreData.score) as totalScore
        WHERE person IS NOT NULL
        ORDER BY totalScore DESC
        RETURN collect(person)[0] as calculatedDri
    }
    RETURN COALESCE(iss.assigned_to, calculatedDri) as dri
  `,
};

export const eisenhowerMatrixQueries = {
  getEisenhowerData: `
    WITH $departmentId AS targetDept, $clientId AS targetClient, $startDate AS startDate, $endDate AS endDate, $driFilter as driFilter, $typeFilter as typeFilter

    OPTIONAL MATCH (deptStaffOptional:Staff)
    WHERE targetDept IS NOT NULL AND deptStaffOptional.department = targetDept
    WITH collect(deptStaffOptional.name) AS deptStaffNames,
         collect(deptStaffOptional.emails) as deptStaffEmailsNested, // Collect lists of emails
         targetDept, targetClient, startDate, endDate,
         CASE WHEN targetDept IS NOT NULL THEN true ELSE false END as isDepartmentQuery,
         driFilter, typeFilter

    // FIX: Flatten the list of email lists to check against all emails
    WITH deptStaffNames, [email IN apoc.coll.flatten(deptStaffEmailsNested) WHERE email IS NOT NULL] as deptStaffEmails,
         targetDept, targetClient, startDate, endDate, isDepartmentQuery, driFilter, typeFilter

    CALL {
        WITH deptStaffNames, deptStaffEmails, targetDept, targetClient, startDate, endDate, isDepartmentQuery, typeFilter
        UNWIND CASE WHEN isDepartmentQuery THEN typeFilter ELSE [] END AS itemLabel
        CALL {
            WITH itemLabel, deptStaffNames, deptStaffEmails, targetDept, startDate, endDate
            MATCH (item) WHERE itemLabel IN labels(item)
            AND (
                (item:Goal AND EXISTS {
                    (comm:Communication)-[:EVIDENCED_BY]->(item)
                    WHERE comm.primary_staff IN deptStaffEmails AND ${commDateFilter('comm', 'startDate', 'endDate')}
                })
                OR (item:Initiative AND EXISTS {
                    (comm:Communication)-[:EVIDENCED_BY]->(item)
                    WHERE comm.primary_staff IN deptStaffEmails AND ${commDateFilter('comm', 'startDate', 'endDate')}
                })
                OR (item:Issue AND (item.reporter IN deptStaffNames OR item.assigned_to IN deptStaffNames OR EXISTS {
                    (comm:Communication)-[:EVIDENCED_BY]->(item)
                    WHERE comm.primary_staff IN deptStaffEmails AND ${commDateFilter('comm', 'startDate', 'endDate')}
                }))
                OR (item:ActionItem AND (item.assigned_to IN deptStaffNames OR EXISTS {
                    (comm:Communication)-[:EVIDENCED_BY]->(item)
                    WHERE comm.primary_staff IN deptStaffEmails AND ${commDateFilter('comm', 'startDate', 'endDate')}
                }))
            )
            AND (
                (item:Goal AND (item.start_date IS NULL OR date(item.start_date) <= date(endDate)) AND (item.status = 'open' OR (item.achieved_date IS NOT NULL AND date(item.achieved_date) >= date(startDate)))) OR
                (item:Initiative AND (item.start_date IS NULL OR date(item.start_date) <= date(endDate)) AND (item.status = 'open' OR (item.completed_date IS NOT NULL AND date(item.completed_date) >= date(startDate)))) OR
                (item:Issue AND (item.created_at IS NULL OR date(item.created_at) <= date(endDate)) AND (item.status = 'open' OR (item.resolved_at IS NOT NULL AND date(item.resolved_at) >= date(startDate)))) OR
                (item:ActionItem AND (item.created_at IS NULL OR date(item.created_at) <= date(endDate)) AND (item.status = 'open' OR (item.resolved_at IS NOT NULL AND date(item.resolved_at) >= date(startDate))))
            )
            RETURN item
        }
        RETURN item
        
        UNION
        
        WITH deptStaffNames, deptStaffEmails, targetDept, targetClient, startDate, endDate, isDepartmentQuery, typeFilter
        UNWIND (CASE
                  WHEN NOT isDepartmentQuery AND targetClient IS NOT NULL THEN [1]
                  ELSE []
                END) AS client_branch_trigger
        MATCH (item)
        WHERE item.client_id = targetClient
          AND size([label IN labels(item) WHERE label IN typeFilter]) > 0
          AND (
            (item:Goal AND (item.start_date IS NULL OR date(item.start_date) <= date(endDate)) AND (item.status = 'open' OR (item.achieved_date IS NOT NULL AND date(item.achieved_date) >= date(startDate)))) OR
            (item:Initiative AND (item.start_date IS NULL OR date(item.start_date) <= date(endDate)) AND (item.status = 'open' OR (item.completed_date IS NOT NULL AND date(item.completed_date) >= date(startDate)))) OR
            (item:Issue AND (item.created_at IS NULL OR date(item.created_at) <= date(endDate)) AND (item.status = 'open' OR (item.resolved_at IS NOT NULL AND date(item.resolved_at) >= date(startDate)))) OR
            (item:ActionItem AND (item.created_at IS NULL OR date(item.created_at) <= date(endDate)) AND (item.status = 'open' OR (item.resolved_at IS NOT NULL AND date(item.resolved_at) >= date(startDate))))
          )
        RETURN item
    }
    
    WITH DISTINCT item, driFilter
    
    CALL {
        WITH item
        WITH item,
            CASE
                WHEN item:ActionItem THEN item.assigned_to
                WHEN item:Goal THEN apoc.cypher.runFirstColumnSingle("
                    MATCH (node:Goal {id: $id})
                    CALL {
                        WITH node
                        OPTIONAL MATCH (node)<-[:SUPPORTS_GOAL]-(:Initiative)<-[:SUPPORTS]-(ai:ActionItem)
                        WHERE ai.assigned_to IS NOT NULL
                        WITH node, collect({person: ai.assigned_to, score: 2}) as actionItemScores
                        OPTIONAL MATCH (comm:Communication)-[:EVIDENCED_BY]->(node)
                        WHERE comm.primary_staff IS NOT NULL
                        MATCH (s:Staff) WHERE comm.primary_staff IN s.emails
                        WITH node, actionItemScores, collect({person: s.name, score: 1}) as commScores
                        UNWIND(actionItemScores + commScores) as scoreData
                        WITH scoreData.person as person, sum(scoreData.score) as totalScore
                        WHERE person IS NOT NULL
                        ORDER BY totalScore DESC
                        RETURN collect(person)[0] as calculatedDri
                    }
                    RETURN COALESCE(node.assigned_to, calculatedDri)
                ", {id: item.id})
                WHEN item:Initiative THEN apoc.cypher.runFirstColumnSingle("
                    MATCH (node:Initiative {id: $id})
                     CALL {
                        WITH node
                        OPTIONAL MATCH (node)<-[:SUPPORTS]-(ai:ActionItem)
                        WHERE ai.assigned_to IS NOT NULL
                        WITH node, collect({person: ai.assigned_to, score: 2}) as actionItemScores
                        OPTIONAL MATCH (comm:Communication)-[:EVIDENCED_BY]->(node)
                        WHERE comm.primary_staff IS NOT NULL
                        MATCH (s:Staff) WHERE comm.primary_staff IN s.emails
                        WITH node, actionItemScores, collect({person: s.name, score: 1}) as commScores
                        UNWIND(actionItemScores + commScores) as scoreData
                        WITH scoreData.person as person, sum(scoreData.score) as totalScore
                        WHERE person IS NOT NULL
                        ORDER BY totalScore DESC
                        RETURN collect(person)[0] as calculatedDri
                    }
                    RETURN COALESCE(node.assigned_to, calculatedDri)
                ", {id: item.id})
                WHEN item:Issue THEN apoc.cypher.runFirstColumnSingle("
                    MATCH (node:Issue {id: $id})
                    CALL {
                        WITH node
                        OPTIONAL MATCH (node)-[:BLOCKS]->(:Initiative)<-[:SUPPORTS]-(ai:ActionItem)
                        WHERE ai.assigned_to IS NOT NULL
                        WITH node, collect({person: ai.assigned_to, score: 2}) as actionItemScores
                        OPTIONAL MATCH (comm:Communication)-[:EVIDENCED_BY]->(node)
                        WHERE comm.primary_staff IS NOT NULL
                        MATCH (s:Staff) WHERE comm.primary_staff IN s.emails
                        WITH node, actionItemScores, collect({person: s.name, score: 1}) as commScores
                        UNWIND(actionItemScores + commScores) as scoreData
                        WITH scoreData.person as person, sum(scoreData.score) as totalScore
                        WHERE person IS NOT NULL
                        ORDER BY totalScore DESC
                        RETURN collect(person)[0] as calculatedDri
                    }
                    RETURN COALESCE(node.assigned_to, calculatedDri)
                ", {id: item.id})
                ELSE null
            END as dri
        RETURN dri
    }
    
    WITH item, dri, driFilter
    WHERE driFilter IS NULL OR size(driFilter) = 0 OR ANY(filterDri IN $driFilter WHERE dri CONTAINS filterDri)

    OPTIONAL MATCH (item)-[rel]-() 
    WITH item, COUNT(rel) AS connectionCount, dri
    
    WITH item, connectionCount, dri,
      CASE
        WHEN item:Issue THEN
          CASE item.severity
            WHEN 'high' THEN 3.0 WHEN 'medium' THEN 2.0 WHEN 'low' THEN 1.0 ELSE 1.5
          END
        WHEN item:ActionItem THEN
          CASE
            WHEN item.status = 'open' AND item.deadline IS NOT NULL AND date(item.deadline) < date() THEN 3.0
            WHEN item.status = 'open' AND item.deadline IS NOT NULL AND date(item.deadline) <= date() + duration({days: 7}) THEN 2.5
            WHEN item.status = 'open' AND item.deadline IS NOT NULL AND date(item.deadline) <= date() + duration({days: 14}) THEN 2.0
            WHEN item.status = 'open' THEN 1.5
            ELSE 0.5
          END
        WHEN item:Goal THEN
          CASE
            WHEN item.target_date IS NOT NULL AND date(item.target_date) < date() THEN 3.0
            WHEN item.target_date IS NOT NULL AND date(item.target_date) <= date() + duration({days: 30}) THEN 2.0
            ELSE 1.0
          END
        WHEN item:Initiative THEN
          CASE item.status
            WHEN 'blocked' THEN 2.5 WHEN 'open' THEN 1.5 WHEN 'open' THEN 1.0 ELSE 0.5
          END
        ELSE 1.0
      END AS urgencyScore,
      CASE
        WHEN item:ActionItem THEN item.assigned_to
        WHEN item:Goal THEN item.status
        WHEN item:Initiative THEN item.status
        WHEN item:Issue THEN item.severity
        ELSE null
      END AS detail1,
      CASE
        WHEN item:ActionItem THEN item.deadline
        WHEN item:Goal THEN item.target_date
        ELSE null
      END AS detail2
    
    RETURN item.id AS id,
           item.name AS name,
           labels(item)[0] AS type,
           connectionCount AS importanceScore,
           round(urgencyScore * 100) / 100 AS urgencyScore,
           detail1,
           detail2,
           item.client_id AS clientId,
           dri
    ORDER BY urgencyScore DESC, importanceScore DESC
    LIMIT 200
  `
};